// v17
// Path: frontend/src/components/SyncHub/DataCatalogStudio.jsx
// Inputs: token (JWT authorization string), onClose (function)
// Outputs: Full-screen React modal providing a 4-tab unified studio for Data Catalog and Vault management
// Summary: Studio component unifying Vault.Credentials physical connections, floating zero-layout-shift toasts, reorderable multi-level grouping (Organization, Host, Instance, App, Type), configured and uncredentialed container visibility, recursive dropdown ConnectionBindingDrawer with edit and cancel support, live schema inspection, regex transforms, and relational catalog mapping.

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import SearchableSelect from './SearchableSelect';
import ConnectionBindingDrawer from './ConnectionBindingDrawer';

export default function DataCatalogStudio({ token, onClose }) {
    const [activeTab, setActiveTab] = useState('connections'); // 'connections', 'transforms', 'entities', 'relationships'
    const [loading, setLoading] = useState(false);
    const [notification, setNotification] = useState(null);

    // Reference data
    const [apps, setApps] = useState([]);
    const [driverTypes, setDriverTypes] = useState([]);
    const [categories, setCategories] = useState([]);
    const [authModels, setAuthModels] = useState([]);
    const [integrationAccounts, setIntegrationAccounts] = useState([]);

    // Tab 1: Connections & Live Inspector
    const [connections, setConnections] = useState([]);
    const [allCredentials, setAllCredentials] = useState([]);
    const [activeGrouping, setActiveGrouping] = useState(['OrganizationName', 'HostDisplayName', 'InstanceName']);
    const [expandedGroups, setExpandedGroups] = useState({});
    const [bindModalContainer, setBindModalContainer] = useState(null);
    const [editingConnection, setEditingConnection] = useState(null);
    const [isCreatingConnection, setIsCreatingConnection] = useState(false);
    const [connectionForm, setConnectionForm] = useState({});
    const [activeInspectConnection, setActiveInspectConnection] = useState(null);
    const [inspectLoading, setInspectLoading] = useState(false);
    const [inspectResult, setInspectResult] = useState(null);
    const [inspectAppId, setInspectAppId] = useState(1);
    const [inspectVersion, setInspectVersion] = useState('12.08');
    const [updatingAppVersion, setUpdatingAppVersion] = useState(false);
    const [connectionAppVersions, setConnectionAppVersions] = useState([]);
    const [inspectAppVersions, setInspectAppVersions] = useState([]);
    const [selectedImportTables, setSelectedImportTables] = useState([]);
    const [expandedTableDiff, setExpandedTableDiff] = useState(null);
    const [importingTables, setImportingTables] = useState(false);

    // Network Targets & Live Connection Testing State
    const [networkTargets, setNetworkTargets] = useState({ devices: [], sqlInstances: [], interfaces: [] });
    const [modalInterfaces, setModalInterfaces] = useState([]);
    const [customIpMode, setCustomIpMode] = useState(false);
    const [testingConnection, setTestingConnection] = useState(false);
    const [modalTestResult, setModalTestResult] = useState(null);
    const [testingRowId, setTestingRowId] = useState(null);

    // Tab 2: Transforms & Simulator
    const [transforms, setTransforms] = useState([]);
    const [editingTransform, setEditingTransform] = useState(null);
    const [isCreatingTransform, setIsCreatingTransform] = useState(false);
    const [transformForm, setTransformForm] = useState({});
    const [simInput, setSimInput] = useState('CUSFIL00');
    const [simPattern, setSimPattern] = useState('^([A-Za-z_]+)\\d{2}$');
    const [simReplacement, setSimReplacement] = useState('$1');
    const [simScope, setSimScope] = useState('TABLE');
    const [simPriority, setSimPriority] = useState(10);
    const [simAppId, setSimAppId] = useState(1);
    const [simAppVersions, setSimAppVersions] = useState([]);
    const [simVersion, setSimVersion] = useState('12.08');
    const [simDescription, setSimDescription] = useState('');
    const [simResult, setSimResult] = useState(null);
    const [showRegexCheatsheet, setShowRegexCheatsheet] = useState(false);

    // Tab 3: Entities, Fields & Categories
    const [selectedAppId, setSelectedAppId] = useState(1);
    const [appVersions, setAppVersions] = useState([]);
    const [selectedVersion, setSelectedVersion] = useState('12.08');
    const [isCustomApp, setIsCustomApp] = useState(false);
    const [customAppName, setCustomAppName] = useState('');
    const [isCustomVersion, setIsCustomVersion] = useState(false);
    const [customVersionText, setCustomVersionText] = useState('');
    const [selectedCategoryId, setSelectedCategoryId] = useState('');
    const [entitySearch, setEntitySearch] = useState('');
    const [entities, setEntities] = useState([]);
    const [selectedEntity, setSelectedEntity] = useState(null);
    const [fields, setFields] = useState([]);
    const [editingFieldId, setEditingFieldId] = useState(null);
    const [editingFieldDescription, setEditingFieldDescription] = useState('');
    const [selectedEntityCatIds, setSelectedEntityCatIds] = useState([]);

    // Tab 4: Relationships
    const [relationships, setRelationships] = useState([]);
    const [relFilterTable, setRelFilterTable] = useState('');
    const [isCreatingRel, setIsCreatingRel] = useState(false);
    const [relForm, setRelForm] = useState({
        RelationshipName: '',
        FromTable: '',
        ToTable: '',
        Cardinality: '1:M',
        JoinType: 'INNER',
        Description: '',
        components: [{ KeyOrdinal: 1, FromColumn: '', ToColumn: '', Delimiter: '' }]
    });

    const authHeaders = useMemo(() => ({
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
    }), [token]);

    const showNotify = (msg, type = 'success') => {
        setNotification({ message: msg, type });
        setTimeout(() => setNotification(null), 4000);
    };

    // Initial load of master references
    useEffect(() => {
        loadMetadata();
    }, []);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                if (bindModalContainer) {
                    setBindModalContainer(null);
                } else if (isCreatingConnection) {
                    setIsCreatingConnection(false);
                    setEditingConnection(null);
                } else if (activeInspectConnection) {
                    setActiveInspectConnection(null);
                } else if (onClose) {
                    onClose();
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [bindModalContainer, isCreatingConnection, activeInspectConnection, onClose]);

    const loadMetadata = async () => {
        try {
            const [appsRes, typesRes, catsRes, targetsRes, authRes, accsRes, credsRes] = await Promise.all([
                fetch('/api/catalog/applications', { headers: authHeaders }),
                fetch('/api/catalog/datasource-types', { headers: authHeaders }),
                fetch('/api/catalog/categories', { headers: authHeaders }),
                fetch('/api/catalog/network-targets', { headers: authHeaders }),
                fetch('/api/catalog/auth-models', { headers: authHeaders }),
                fetch('/api/catalog/integration-accounts', { headers: authHeaders }),
                fetch('/api/catalog/credentials', { headers: authHeaders })
            ]);
            if (appsRes.ok) {
                const a = await appsRes.json();
                setApps(a.data || []);
            }
            if (typesRes.ok) {
                const t = await typesRes.json();
                setDriverTypes(t.data || []);
            }
            if (catsRes.ok) {
                const c = await catsRes.json();
                setCategories(c.data || []);
            }
            if (targetsRes.ok) {
                const tg = await targetsRes.json();
                setNetworkTargets(tg.data || { devices: [], sqlInstances: [], interfaces: [] });
            }
            if (authRes.ok) {
                const am = await authRes.json();
                setAuthModels(am.data || []);
            }
            if (accsRes.ok) {
                const ia = await accsRes.json();
                setIntegrationAccounts(ia.data || []);
            }
            if (credsRes && credsRes.ok) {
                const cr = await credsRes.json();
                setAllCredentials(cr.data || []);
            }
        } catch (err) {
            console.error('[DataCatalogStudio] Failed to load metadata:', err);
        }
    };

    // Continuous Live Regex Simulation on Keystroke
    useEffect(() => {
        if (!simPattern) {
            setSimResult(null);
            return;
        }
        try {
            const regex = new RegExp(simPattern, 'i');
            const match = simInput ? simInput.match(regex) : null;
            const isMatch = !!match;
            const captureGroups = (isMatch && match.length > 1) ? Array.from(match).slice(1) : [];
            const hasGroupTokens = /\$[1-9]/.test(simReplacement);
            const missingGroupsWarning = isMatch && hasGroupTokens && captureGroups.length === 0;

            const transformed = isMatch ? simInput.replace(regex, simReplacement) : simInput;
            setSimResult({ 
                isMatch, 
                transformed, 
                captureGroups, 
                missingGroupsWarning,
                error: null 
            });
        } catch (err) {
            setSimResult({ isMatch: false, transformed: simInput, captureGroups: [], missingGroupsWarning: false, error: err.message });
        }
    }, [simPattern, simReplacement, simInput]);

    // Load Application Versions for selectedAppId
    useEffect(() => {
        if (!selectedAppId) return;
        fetch(`/api/catalog/applications/${selectedAppId}/versions`, { headers: authHeaders })
            .then(res => res.json())
            .then(data => {
                if (data.success && data.data) {
                    setAppVersions(data.data);
                    if (data.data.length > 0 && !data.data.some(v => v.VersionNumber === selectedVersion)) {
                        setSelectedVersion(data.data[0].VersionNumber);
                    }
                }
            })
            .catch(e => console.error('[DataCatalogStudio] Failed to load app versions:', e));
    }, [selectedAppId]);

    // Load Application Versions for simAppId in Tab 2
    useEffect(() => {
        if (!simAppId) return;
        fetch(`/api/catalog/applications/${simAppId}/versions`, { headers: authHeaders })
            .then(res => res.json())
            .then(data => {
                if (data.success && data.data) {
                    setSimAppVersions(data.data);
                }
            })
            .catch(e => console.error('[DataCatalogStudio] Failed to load sim app versions:', e));
    }, [simAppId]);

    // Load Application Versions for connectionForm.SourceAppID
    useEffect(() => {
        const appId = connectionForm.SourceAppID;
        if (!appId) {
            setConnectionAppVersions([]);
            return;
        }
        fetch(`/api/catalog/applications/${appId}/versions`, { headers: authHeaders })
            .then(res => res.json())
            .then(data => {
                if (data.success && data.data) {
                    setConnectionAppVersions(data.data);
                } else {
                    setConnectionAppVersions([]);
                }
            })
            .catch(() => setConnectionAppVersions([]));
    }, [connectionForm.SourceAppID]);

    // Load Application Versions for inspectAppId in Live Inspector
    useEffect(() => {
        if (!inspectAppId) {
            setInspectAppVersions([]);
            return;
        }
        fetch(`/api/catalog/applications/${inspectAppId}/versions`, { headers: authHeaders })
            .then(res => res.json())
            .then(data => {
                if (data.success && data.data) {
                    setInspectAppVersions(data.data);
                } else {
                    setInspectAppVersions([]);
                }
            })
            .catch(() => setInspectAppVersions([]));
    }, [inspectAppId]);

    // Reusable Application Options for SearchableSelect
    const appOptions = useMemo(() => {
        return apps.map(a => ({
            value: a.SourceAppID,
            label: a.AppName,
            description: a.AppCode
        }));
    }, [apps]);

    // Connection Form Version Options
    const connectionVersionOptions = useMemo(() => {
        const opts = [
            { value: '', label: '(None / Not Versioned)', description: 'No specific version applies' }
        ];
        for (const v of connectionAppVersions) {
            opts.push({
                value: v.VersionNumber,
                label: `v${v.VersionNumber}`,
                description: v.VersionName || undefined
            });
        }
        if (connectionForm.AppVersion && !opts.some(o => o.value === connectionForm.AppVersion)) {
            opts.push({
                value: connectionForm.AppVersion,
                label: `v${connectionForm.AppVersion}`,
                isCustom: true
            });
        }
        return opts;
    }, [connectionAppVersions, connectionForm.AppVersion]);

    // Live Inspector Version Options
    const inspectVersionOptions = useMemo(() => {
        const opts = [
            { value: '', label: '(None / Not Versioned)', description: 'No specific version applies' }
        ];
        for (const v of inspectAppVersions) {
            opts.push({
                value: v.VersionNumber,
                label: `v${v.VersionNumber}`,
                description: v.VersionName || undefined
            });
        }
        if (inspectVersion && !opts.some(o => o.value === inspectVersion)) {
            opts.push({
                value: inspectVersion,
                label: `v${inspectVersion}`,
                isCustom: true
            });
        }
        return opts;
    }, [inspectAppVersions, inspectVersion]);

    // Transforms Simulator Version Options
    const simVersionOptions = useMemo(() => {
        const opts = [
            { value: '*', label: '* (All Versions / Global)', description: 'Applies across all versions' },
            { value: '', label: '(None / Not Versioned)', description: 'Unversioned transform' }
        ];
        for (const v of simAppVersions) {
            opts.push({
                value: v.VersionNumber,
                label: `v${v.VersionNumber}`,
                description: v.VersionName || undefined
            });
        }
        if (simVersion && !opts.some(o => o.value === simVersion)) {
            opts.push({
                value: simVersion,
                label: `v${simVersion}`,
                isCustom: true
            });
        }
        return opts;
    }, [simAppVersions, simVersion]);

    // Entities Version Options
    const entityVersionOptions = useMemo(() => {
        const opts = [];
        for (const v of appVersions) {
            opts.push({
                value: v.VersionNumber,
                label: `v${v.VersionNumber}`,
                description: v.VersionName || undefined
            });
        }
        if (selectedVersion && !opts.some(o => o.value === selectedVersion)) {
            opts.push({
                value: selectedVersion,
                label: `v${selectedVersion}`,
                isCustom: true
            });
        }
        return opts;
    }, [appVersions, selectedVersion]);

    // Load data when active tab changes
    useEffect(() => {
        if (activeTab === 'connections') loadConnections();
        if (activeTab === 'transforms') loadTransforms();
        if (activeTab === 'entities') loadEntities();
        if (activeTab === 'relationships') loadRelationships();
    }, [activeTab, selectedAppId, selectedVersion, selectedCategoryId]);

    // -------------------------------------------------------------------------
    // TAB 1: Connections & Data Containers
    // -------------------------------------------------------------------------
    const GROUP_DIMENSIONS = useMemo(() => [
        { key: 'OrganizationName', label: 'Organization', icon: 'corporate_fare' },
        { key: 'HostDisplayName', label: 'Host', icon: 'computer' },
        { key: 'InstanceName', label: 'Instance', icon: 'dns' },
        { key: 'SourceAppName', label: 'App', icon: 'apps' },
        { key: 'ContainerType', label: 'Type', icon: 'layers' }
    ], []);

    const loadConnections = async () => {
        setLoading(true);
        try {
            const [connRes, credRes] = await Promise.all([
                fetch('/api/catalog/connections', { headers: authHeaders }),
                fetch('/api/catalog/credentials', { headers: authHeaders })
            ]);
            const data = await connRes.json();
            if (data.success) setConnections(data.data || []);
            if (credRes && credRes.ok) {
                const cr = await credRes.json();
                setAllCredentials(cr.data || []);
            }
        } catch (err) {
            showNotify('Failed to load connections: ' + err.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const moveGroupingLeft = (idx) => {
        if (idx <= 0) return;
        const next = [...activeGrouping];
        const tmp = next[idx - 1];
        next[idx - 1] = next[idx];
        next[idx] = tmp;
        setActiveGrouping(next);
        setExpandedGroups({});
    };

    const moveGroupingRight = (idx) => {
        if (idx >= activeGrouping.length - 1) return;
        const next = [...activeGrouping];
        const tmp = next[idx + 1];
        next[idx + 1] = next[idx];
        next[idx] = tmp;
        setActiveGrouping(next);
        setExpandedGroups({});
    };

    const removeGrouping = (key) => {
        setActiveGrouping(prev => prev.filter(k => k !== key));
        setExpandedGroups({});
    };

    const addGrouping = (key) => {
        if (!activeGrouping.includes(key)) {
            setActiveGrouping(prev => [...prev, key]);
            setExpandedGroups({});
        }
    };

    const toggleGroupCollapse = (pathKey) => {
        setExpandedGroups(prev => {
            const currentlyExpanded = prev['__all'] === true ? (prev[pathKey] !== false) : !!prev[pathKey];
            return {
                ...prev,
                [pathKey]: !currentlyExpanded
            };
        });
    };

    // Live Scope Handlers: Re-inspect and update KPIs immediately upon Application or Version scope change
    const handleInspectAppChange = async (val) => {
        const newAppId = parseInt(val, 10);
        setInspectAppId(newAppId);
        let chosenVersion = '';
        try {
            const res = await fetch(`/api/catalog/applications/${newAppId}/versions`, { headers: authHeaders });
            const data = await res.json();
            if (data.success && data.data && data.data.length > 0) {
                setInspectAppVersions(data.data);
                chosenVersion = data.data[0].VersionNumber;
            } else {
                setInspectAppVersions([]);
            }
        } catch (e) {
            console.error('[DataCatalogStudio] Error loading versions for inspect app:', e);
        }
        setInspectVersion(chosenVersion);
        if (activeInspectConnection) {
            handleInspectConnection(activeInspectConnection, newAppId, chosenVersion);
        }
    };

    const handleInspectVersionChange = (newVersion) => {
        setInspectVersion(newVersion);
        if (activeInspectConnection) {
            handleInspectConnection(activeInspectConnection, inspectAppId, newVersion);
        }
    };

    const handleUnbindCredential = async (bindingId) => {
        if (!bindingId) return;
        if (!window.confirm('Are you sure you want to unbind this credential from the container?')) return;
        try {
            const res = await fetch(`/api/catalog/bindings/${bindingId}`, {
                method: 'DELETE',
                headers: authHeaders
            });
            const data = await res.json();
            if (data.success) {
                showNotify('Credential unbound.');
                loadConnections();
            } else {
                showNotify(data.error || 'Failed to unbind credential', 'error');
            }
        } catch (err) {
            showNotify(err.message, 'error');
        }
    };

    const handleSaveConnection = async (e) => {
        e.preventDefault();
        try {
            const isEdit = !!editingConnection;
            const url = isEdit ? `/api/catalog/connections/${editingConnection.CredentialID}` : '/api/catalog/connections';
            const method = isEdit ? 'PUT' : 'POST';

            const payload = {
                ConnectionName: connectionForm.ConnectionName,
                SourceAppID: connectionForm.SourceAppID,
                SourceTypeID: connectionForm.SourceTypeID,
                CredentialTypeID: connectionForm.CredentialTypeID,
                AuthModelID: connectionForm.AuthModelID,
                AppVersion: connectionForm.AppVersion,
                HostServer: connectionForm.HostServer || connectionForm.baseUrl || '127.0.0.1',
                HostPort: connectionForm.HostPort ? parseInt(connectionForm.HostPort, 10) : (connectionForm.SourceTypeID === 2 ? 443 : 1433),
                DatabaseName: connectionForm.DatabaseName || connectionForm.realmId || '',
                Username: connectionForm.Username || (connectionForm.SourceTypeID === 2 ? 'QuickBooks Sandbox' : 'sa'),
                EncryptedPassword: connectionForm.EncryptedPassword,
                linkedAccountId: connectionForm.linkedAccountId,
                config: {
                    linkedAccountId: connectionForm.linkedAccountId,
                    baseUrl: connectionForm.baseUrl || connectionForm.HostServer,
                    realmId: connectionForm.realmId || connectionForm.DatabaseName,
                    clientId: connectionForm.clientId,
                    clientSecret: connectionForm.clientSecret,
                    accessToken: connectionForm.accessToken,
                    refreshToken: connectionForm.refreshToken,
                    headerName: connectionForm.headerName,
                    apiKey: connectionForm.apiKey,
                    domain: connectionForm.domain,
                    privateKey: connectionForm.privateKey,
                    hostServer: connectionForm.HostServer,
                    hostPort: connectionForm.HostPort,
                    databaseName: connectionForm.DatabaseName,
                    username: connectionForm.Username
                }
            };

            const res = await fetch(url, {
                method,
                headers: authHeaders,
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (data.success) {
                showNotify(`Connection ${isEdit ? 'updated' : 'created'} successfully!`);
                setIsCreatingConnection(false);
                setEditingConnection(null);
                loadConnections();
            } else {
                showNotify(data.error || 'Failed to save connection', 'error');
            }
        } catch (err) {
            showNotify(err.message, 'error');
        }
    };

    const handleDeleteConnection = async (id) => {
        if (!window.confirm('Are you sure you want to delete this connection instance?')) return;
        try {
            const res = await fetch(`/api/catalog/connections/${id}`, { method: 'DELETE', headers: authHeaders });
            const data = await res.json();
            if (data.success) {
                showNotify('Connection deleted.');
                loadConnections();
            }
        } catch (err) {
            showNotify(err.message, 'error');
        }
    };

    const handleInspectConnection = async (conn, customAppId = null, customVersion = null) => {
        setIsCreatingConnection(false);
        setEditingConnection(null);
        setActiveInspectConnection(conn);
        const targetAppId = customAppId != null ? customAppId : (conn.SourceAppID || 1);
        const targetVersion = customVersion != null ? customVersion : (conn.AppVersion || '');
        setInspectAppId(targetAppId);
        setInspectVersion(targetVersion);
        setInspectLoading(true);
        setInspectResult(null);
        setSelectedImportTables([]);
        setExpandedTableDiff(null);

        try {
            const targetId = conn.ContainerID || conn.CredentialID;
            const isCont = Boolean(conn.ContainerID);
            const url = `/api/catalog/connections/${targetId}/inspect?appId=${targetAppId}&version=${encodeURIComponent(targetVersion || '')}${isCont ? '&isContainer=true' : ''}`;
            const res = await fetch(url, { headers: authHeaders });
            const data = await res.json();
            if (data.success) {
                setInspectResult(data.data);
                if (data.data?.status === 'CONNECTED') {
                    showNotify(`Live Inspection: ${data.data.totalLiveColumns} live columns analyzed.`);
                } else if (data.data?.status === 'UNSUPPORTED') {
                    showNotify(data.data.error, 'info');
                } else {
                    showNotify(data.data?.error || 'Live database unreachable', 'error');
                }
            } else {
                showNotify(data.error || 'Inspection failed', 'error');
            }
        } catch (err) {
            showNotify(err.message, 'error');
        } finally {
            setInspectLoading(false);
        }
    };

    const handleSaveInspectAppVersion = async () => {
        if (!activeInspectConnection) return;
        setUpdatingAppVersion(true);
        try {
            const res = await fetch(`/api/catalog/connections/${activeInspectConnection.CredentialID}`, {
                method: 'PUT',
                headers: authHeaders,
                body: JSON.stringify({
                    SourceAppID: inspectAppId,
                    AppVersion: inspectVersion
                })
            });
            const data = await res.json();
            if (data.success) {
                showNotify('Updated connection App and Version!');
                loadConnections();
                const updatedConn = { ...activeInspectConnection, SourceAppID: inspectAppId, AppVersion: inspectVersion };
                setActiveInspectConnection(updatedConn);
                handleInspectConnection(updatedConn, inspectAppId, inspectVersion);
            } else {
                showNotify(data.error || 'Failed to update App / Version', 'error');
            }
        } catch (err) {
            showNotify(err.message, 'error');
        } finally {
            setUpdatingAppVersion(false);
        }
    };

    const handleImportSelectedTables = async () => {
        if (!activeInspectConnection || selectedImportTables.length === 0) return;
        setImportingTables(true);
        try {
            const res = await fetch('/api/catalog/import-tables', {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({
                    connectionId: activeInspectConnection.ContainerID || activeInspectConnection.CredentialID,
                    sourceAppId: inspectAppId,
                    appVersion: inspectVersion,
                    tables: selectedImportTables
                })
            });
            const data = await res.json();
            if (data.success) {
                showNotify(`Imported ${data.importedTablesCount} tables (${data.importedFieldsCount} fields) into Catalog!`);
                setSelectedImportTables([]);
                handleInspectConnection(activeInspectConnection, inspectAppId, inspectVersion);
            } else {
                showNotify(data.error || 'Import failed', 'error');
            }
        } catch (err) {
            showNotify(err.message, 'error');
        } finally {
            setImportingTables(false);
        }
    };

    const handleTestConnection = async (targetId = null, customParams = null, isContainer = false) => {
        if (targetId) setTestingRowId(targetId);
        else setTestingConnection(true);
        if (!targetId) setModalTestResult(null);

        try {
            const url = targetId ? `/api/catalog/connections/${targetId}/test${isContainer ? '?isContainer=true' : ''}` : '/api/catalog/connections/test';
            const body = customParams ? JSON.stringify({
                SourceTypeID: customParams.SourceTypeID,
                AuthModelID: customParams.AuthModelID,
                HostServer: customParams.baseUrl || customParams.HostServer,
                HostPort: customParams.HostPort,
                DatabaseName: customParams.realmId || customParams.DatabaseName,
                Username: customParams.Username,
                linkedAccountId: customParams.linkedAccountId,
                host: customParams.HostServer,
                port: customParams.HostPort,
                username: customParams.Username,
                password: customParams.EncryptedPassword,
                databaseName: customParams.DatabaseName,
                config: {
                    linkedAccountId: customParams.linkedAccountId,
                    baseUrl: customParams.baseUrl || customParams.HostServer,
                    realmId: customParams.realmId || customParams.DatabaseName,
                    clientId: customParams.clientId,
                    clientSecret: customParams.clientSecret,
                    accessToken: customParams.accessToken,
                    refreshToken: customParams.refreshToken,
                    headerName: customParams.headerName,
                    apiKey: customParams.apiKey
                }
            }) : JSON.stringify({});

            const res = await fetch(url, {
                method: 'POST',
                headers: authHeaders,
                body
            });
            const data = await res.json();
            if (data.success) {
                const msg = data.message || 'Connection verified successfully!';
                if (!credentialId) setModalTestResult({ success: true, message: msg, databases: data.databases || [] });
                showNotify(msg, 'success');
            } else {
                const errMsg = data.error || 'Connection test failed';
                if (!credentialId) setModalTestResult({ success: false, error: errMsg });
                showNotify(errMsg, 'error');
            }
        } catch (err) {
            if (!credentialId) setModalTestResult({ success: false, error: err.message });
            showNotify(err.message, 'error');
        } finally {
            setTestingConnection(false);
            setTestingRowId(null);
        }
    };



    // -------------------------------------------------------------------------
    // TAB 2: Transforms
    // -------------------------------------------------------------------------
    const loadTransforms = async () => {
        setLoading(true);
        try {
            const res = await fetch('/api/catalog/transforms', { headers: authHeaders });
            const data = await res.json();
            if (data.success) setTransforms(data.data || []);
        } catch (err) {
            showNotify('Failed to load transforms: ' + err.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectTransform = (t) => {
        setEditingTransform(t);
        setSimPattern(t.SearchPattern || '');
        setSimReplacement(t.ReplacementPattern || '');
        setSimScope(t.TransformScope || 'TABLE');
        setSimPriority(t.PriorityOrder || 10);
        setSimAppId(t.SourceAppID || 1);
        setSimVersion(t.AppVersion || '*');
        setSimDescription(t.Description || '');
        if (t.TransformScope === 'DATABASE' && (!simInput || simInput === 'CUSFIL00')) {
            setSimInput('pbsdata00');
        } else if (t.TransformScope === 'TABLE' && (!simInput || simInput === 'pbsdata00')) {
            setSimInput('CUSFIL00');
        }
        showNotify(`Loaded rule #${t.TransformID} into editor!`);
    };

    const handleUpdateActiveTransform = async () => {
        if (!editingTransform) return;
        try {
            const res = await fetch(`/api/catalog/transforms/${editingTransform.TransformID}`, {
                method: 'PUT',
                headers: authHeaders,
                body: JSON.stringify({
                    SourceAppID: parseInt(simAppId, 10),
                    AppVersion: simVersion,
                    TransformScope: simScope,
                    SearchPattern: simPattern,
                    ReplacementPattern: simReplacement,
                    PriorityOrder: parseInt(simPriority, 10) || 10,
                    Description: simDescription
                })
            });
            const data = await res.json();
            if (data.success) {
                showNotify('Transform rule updated successfully!');
                loadTransforms();
            } else {
                showNotify(data.error || 'Failed to update rule', 'error');
            }
        } catch (err) {
            showNotify(err.message, 'error');
        }
    };

    const handleSaveNewTransform = async () => {
        try {
            const res = await fetch('/api/catalog/transforms', {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({
                    SourceAppID: parseInt(simAppId, 10),
                    AppVersion: simVersion,
                    TransformScope: simScope,
                    SearchPattern: simPattern,
                    ReplacementPattern: simReplacement,
                    PriorityOrder: parseInt(simPriority, 10) || 10,
                    Description: simDescription
                })
            });
            const data = await res.json();
            if (data.success) {
                showNotify('New transform rule created!');
                setEditingTransform(data.data);
                loadTransforms();
            } else {
                showNotify(data.error || 'Failed to create rule', 'error');
            }
        } catch (err) {
            showNotify(err.message, 'error');
        }
    };

    const handleResetSimulator = () => {
        setEditingTransform(null);
        setSimPattern('^([A-Za-z_]+)\\d{2}$');
        setSimReplacement('$1');
        setSimScope('TABLE');
        setSimPriority(10);
        setSimAppId(apps[0]?.SourceAppID || 1);
        setSimVersion('*');
        setSimDescription('');
        setSimInput('CUSFIL00');
    };

    const handleDeleteTransform = async (id) => {
        if (!window.confirm('Delete this transform rule?')) return;
        try {
            const res = await fetch(`/api/catalog/transforms/${id}`, { method: 'DELETE', headers: authHeaders });
            const data = await res.json();
            if (data.success) {
                showNotify('Transform deleted.');
                if (editingTransform?.TransformID === id) {
                    handleResetSimulator();
                }
                loadTransforms();
            }
        } catch (err) {
            showNotify(err.message, 'error');
        }
    };

    // -------------------------------------------------------------------------
    // TAB 3: Entities & Fields
    // -------------------------------------------------------------------------
    const loadEntities = async () => {
        setLoading(true);
        try {
            let url = `/api/catalog/entities?sourceAppId=${selectedAppId}&version=${encodeURIComponent(selectedVersion)}`;
            if (selectedCategoryId) url += `&categoryId=${selectedCategoryId}`;
            const res = await fetch(url, { headers: authHeaders });
            const data = await res.json();
            if (data.success) {
                const list = data.data || [];
                setEntities(list);
                if (list.length > 0) {
                    const currentSelected = selectedEntity && list.find(e => (e.EntityCode || e.AbstractTableName) === selectedEntity);
                    const toSelect = currentSelected ? selectedEntity : (list[0].EntityCode || list[0].AbstractTableName);
                    selectEntity(toSelect, list);
                } else {
                    setSelectedEntity(null);
                    setFields([]);
                    setSelectedEntityCatIds([]);
                }
            }
        } catch (err) {
            showNotify('Failed to load entities: ' + err.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const selectEntity = async (tableName, listOverride = null) => {
        setSelectedEntity(tableName);
        setEditingFieldId(null);
        const list = listOverride || entities;
        const ent = list.find(e => (e.EntityCode || e.AbstractTableName) === tableName);
        if (ent) {
            let catIds = [];
            if (ent.CategoryIDsList) {
                catIds = ent.CategoryIDsList.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n));
            } else if (ent.CategoriesList) {
                const catNames = ent.CategoriesList.split(',').map(s => s.trim().toLowerCase());
                catIds = categories.filter(c => catNames.includes(c.CategoryName.toLowerCase())).map(c => c.CategoryID);
            }
            setSelectedEntityCatIds(catIds);
        } else {
            setSelectedEntityCatIds([]);
        }
        try {
            const res = await fetch(`/api/catalog/fields/${tableName}?sourceAppId=${selectedAppId}&version=${encodeURIComponent(selectedVersion)}`, { headers: authHeaders });
            const data = await res.json();
            if (data.success) setFields(data.data || []);
        } catch (err) {
            showNotify('Failed to load fields: ' + err.message, 'error');
        }
    };

    const handleSaveField = async (fieldId, isNullable) => {
        try {
            const res = await fetch(`/api/catalog/fields/${fieldId}`, {
                method: 'PUT',
                headers: authHeaders,
                body: JSON.stringify({
                    FieldDescription: editingFieldDescription,
                    IsNullable: isNullable
                })
            });
            const data = await res.json();
            if (data.success) {
                showNotify('Field updated.');
                setEditingFieldId(null);
                if (selectedEntity) selectEntity(selectedEntity);
            }
        } catch (err) {
            showNotify(err.message, 'error');
        }
    };

    const handleToggleCategory = async (catId, targetEntity = null) => {
        const entity = targetEntity || entities.find(e => (e.EntityCode || e.AbstractTableName) === selectedEntity);
        if (!entity) {
            showNotify('Please select an entity first', 'info');
            return;
        }
        const entityId = entity.EntityID;
        const isAssigned = selectedEntityCatIds.includes(catId);
        try {
            if (isAssigned) {
                const res = await fetch(`/api/catalog/entities/${entityId}/categories/${catId}`, {
                    method: 'DELETE',
                    headers: authHeaders
                });
                const data = await res.json();
                if (data.success) {
                    setSelectedEntityCatIds(prev => prev.filter(id => id !== catId));
                    showNotify('Category removed.');
                    loadEntities();
                } else {
                    showNotify(data.error || 'Failed to remove category', 'error');
                }
            } else {
                const res = await fetch(`/api/catalog/entities/${entityId}/categories`, {
                    method: 'POST',
                    headers: authHeaders,
                    body: JSON.stringify({ categoryId: catId })
                });
                const data = await res.json();
                if (data.success) {
                    setSelectedEntityCatIds(prev => [...prev, catId]);
                    showNotify('Category assigned!');
                    loadEntities();
                } else {
                    showNotify(data.error || 'Failed to assign category', 'error');
                }
            }
        } catch (err) {
            showNotify(err.message, 'error');
        }
    };

    const handleCreateCustomApp = async () => {
        if (!customAppName.trim()) return;
        try {
            const res = await fetch('/api/catalog/applications', {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({ AppName: customAppName.trim() })
            });
            const data = await res.json();
            if (data.success && data.data) {
                showNotify(`Created application "${customAppName.trim()}"!`);
                await loadMetadata();
                setSelectedAppId(data.data.SourceAppID);
                setIsCustomApp(false);
                setCustomAppName('');
            } else {
                showNotify(data.error || 'Failed to create application', 'error');
            }
        } catch (err) {
            showNotify(err.message, 'error');
        }
    };

    const handleCreateCustomVersion = async () => {
        if (!customVersionText.trim()) return;
        try {
            const res = await fetch(`/api/catalog/applications/${selectedAppId}/versions`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({ VersionNumber: customVersionText.trim() })
            });
            const data = await res.json();
            if (data.success && data.data) {
                showNotify(`Created version "${customVersionText.trim()}"!`);
                setSelectedVersion(customVersionText.trim());
                setIsCustomVersion(false);
                setCustomVersionText('');
                const vRes = await fetch(`/api/catalog/applications/${selectedAppId}/versions`, { headers: authHeaders });
                const vData = await vRes.json();
                if (vData.success && vData.data) setAppVersions(vData.data);
            } else {
                showNotify(data.error || 'Failed to create version', 'error');
            }
        } catch (err) {
            showNotify(err.message, 'error');
        }
    };

    const handleDeleteVersion = async (versionObj) => {
        if (!versionObj) return;
        const confirmMsg = `Are you sure you want to delete version "${versionObj.VersionNumber}" and ALL of its associated entities, fields, relationships, and transforms?\n\nThis action is PERMANENT.`;
        if (!window.confirm(confirmMsg)) return;

        try {
            const res = await fetch(`/api/catalog/versions/${versionObj.AppVersionID}`, {
                method: 'DELETE',
                headers: authHeaders
            });
            const data = await res.json();
            if (data.success) {
                showNotify(`Deleted version "${versionObj.VersionNumber}" and all descendants!`);
                const vRes = await fetch(`/api/catalog/applications/${selectedAppId}/versions`, { headers: authHeaders });
                const vData = await vRes.json();
                if (vData.success && vData.data) {
                    setAppVersions(vData.data);
                    if (vData.data.length > 0) {
                        setSelectedVersion(vData.data[0].VersionNumber);
                    } else {
                        setSelectedVersion('');
                    }
                }
                setSelectedEntity(null);
                setFields([]);
                loadEntities();
            } else {
                showNotify(data.error || 'Failed to delete version', 'error');
            }
        } catch (err) {
            showNotify(err.message, 'error');
        }
    };

    const handleDeleteEntity = async (entityId, entityCode) => {
        if (!entityId) return;
        const confirmMsg = `Are you sure you want to delete entity "${entityCode}" and all of its associated fields and relationships?\n\nThis action cannot be undone.`;
        if (!window.confirm(confirmMsg)) return;

        try {
            const res = await fetch(`/api/catalog/entities/${entityId}`, {
                method: 'DELETE',
                headers: authHeaders
            });
            const data = await res.json();
            if (data.success) {
                showNotify(`Deleted entity "${entityCode}"!`);
                if (selectedEntity === entityCode) {
                    setSelectedEntity(null);
                    setFields([]);
                }
                loadEntities();
            } else {
                showNotify(data.error || 'Failed to delete entity', 'error');
            }
        } catch (err) {
            showNotify(err.message, 'error');
        }
    };

    const handleDeleteField = async (fieldId, columnName) => {
        if (!fieldId) return;
        if (!window.confirm(`Are you sure you want to delete field "${columnName}"?`)) return;

        try {
            const res = await fetch(`/api/catalog/fields/${fieldId}`, {
                method: 'DELETE',
                headers: authHeaders
            });
            const data = await res.json();
            if (data.success) {
                showNotify(`Deleted field "${columnName}"!`);
                setFields(prev => prev.filter(f => f.FieldID !== fieldId));
                setEntities(prev => prev.map(e => {
                    const code = e.EntityCode || e.AbstractTableName;
                    if (code === selectedEntity) {
                        return { ...e, FieldCount: Math.max(0, (e.FieldCount || 1) - 1) };
                    }
                    return e;
                }));
            } else {
                showNotify(data.error || 'Failed to delete field', 'error');
            }
        } catch (err) {
            showNotify(err.message, 'error');
        }
    };

    // -------------------------------------------------------------------------
    // TAB 4: Relationships
    // -------------------------------------------------------------------------
    const loadRelationships = async () => {
        setLoading(true);
        try {
            let url = `/api/catalog/relationships?sourceAppId=${selectedAppId}&version=${encodeURIComponent(selectedVersion)}`;
            if (relFilterTable) url += `&abstractTable=${encodeURIComponent(relFilterTable)}`;
            const res = await fetch(url, { headers: authHeaders });
            const data = await res.json();
            if (data.success) setRelationships(data.data || []);
        } catch (err) {
            showNotify('Failed to load relationships: ' + err.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveRelationship = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...relForm,
                SourceAppID: selectedAppId,
                AppVersion: selectedVersion
            };
            const res = await fetch('/api/catalog/relationships', {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (data.success) {
                showNotify('Relationship created!');
                setIsCreatingRel(false);
                setRelForm({
                    RelationshipName: '',
                    FromTable: '',
                    ToTable: '',
                    Cardinality: '1:M',
                    JoinType: 'INNER',
                    Description: '',
                    components: [{ KeyOrdinal: 1, FromColumn: '', ToColumn: '', Delimiter: '' }]
                });
                loadRelationships();
            }
        } catch (err) {
            showNotify(err.message, 'error');
        }
    };

    const handleDeleteRelationship = async (id) => {
        if (!window.confirm('Delete this relationship definition?')) return;
        try {
            const res = await fetch(`/api/catalog/relationships/${id}`, { method: 'DELETE', headers: authHeaders });
            const data = await res.json();
            if (data.success) {
                showNotify('Relationship deleted.');
                loadRelationships();
            }
        } catch (err) {
            showNotify(err.message, 'error');
        }
    };

    // -------------------------------------------------------------------------
    // RENDER UI
    // -------------------------------------------------------------------------
    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-3 animate-fade-in">
            <div className="bg-slate-900 border border-slate-700 w-full max-w-[96vw] h-[92vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden text-slate-100">
                {/* Header */}
                <div className="px-6 py-4 border-b border-slate-800 bg-slate-950/60 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-emerald-400">
                            <span className="material-symbols-outlined text-2xl">menu_book</span>
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-white flex items-center gap-2">
                                Data Catalog & Connection Studio
                                <span className="text-[11px] bg-slate-800 text-emerald-400 px-2.5 py-0.5 rounded-full border border-slate-700 font-mono">
                                    Unified v3
                                </span>
                            </h2>
                            <p className="text-xs text-slate-400">
                                Define abstract entities, manage Vault connection instances, configure regex transforms, and assemble complex joins.
                            </p>
                        </div>
                    </div>

                    {/* Navigation Tabs */}
                    <div className="flex items-center gap-1 bg-slate-950 p-1 rounded-xl border border-slate-800">
                        <button
                            onClick={() => setActiveTab('connections')}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${activeTab === 'connections' ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'}`}
                        >
                            <span className="material-symbols-outlined text-[16px]">vpn_key</span>
                            Connections & Inspector
                        </button>
                        <button
                            onClick={() => setActiveTab('transforms')}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${activeTab === 'transforms' ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'}`}
                        >
                            <span className="material-symbols-outlined text-[16px]">transform</span>
                            Transforms & Regex
                        </button>
                        <button
                            onClick={() => setActiveTab('entities')}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${activeTab === 'entities' ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'}`}
                        >
                            <span className="material-symbols-outlined text-[16px]">table_chart</span>
                            Entities, Fields & Tags
                        </button>
                        <button
                            onClick={() => setActiveTab('relationships')}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${activeTab === 'relationships' ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'}`}
                        >
                            <span className="material-symbols-outlined text-[16px]">hub</span>
                            Complex Relationships
                        </button>
                    </div>

                    <button
                        onClick={onClose}
                        className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                        title="Close Studio (Esc)"
                    >
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                {/* Floating Toast Notification (Zero Layout Shift) */}
                {notification && (
                    <div className="fixed bottom-6 right-6 z-[100] max-w-md animate-fade-in pointer-events-auto">
                        <div className={`px-4 py-3 text-xs rounded-xl shadow-2xl flex items-center gap-2.5 border backdrop-blur-md ${
                            notification.type === 'error'
                                ? 'bg-red-950/90 text-red-200 border-red-800/80 shadow-red-950/50'
                                : notification.type === 'info'
                                ? 'bg-sky-950/90 text-sky-200 border-sky-800/80 shadow-sky-950/50'
                                : 'bg-emerald-950/90 text-emerald-200 border-emerald-800/80 shadow-emerald-950/50'
                        }`}>
                            <span className="material-symbols-outlined text-[18px] shrink-0">
                                {notification.type === 'error' ? 'error' : notification.type === 'info' ? 'info' : 'check_circle'}
                            </span>
                            <div className="flex-1 font-medium">{notification.message}</div>
                            <button
                                onClick={() => setNotification(null)}
                                className="text-slate-400 hover:text-white p-0.5 rounded transition-colors"
                            >
                                <span className="material-symbols-outlined text-[14px]">close</span>
                            </button>
                        </div>
                    </div>
                )}

                {/* Main Tab Body */}
                <div className="flex-1 overflow-hidden p-6 bg-slate-900/60">
                    {/* ========================================================= */}
                    {/* TAB 1: CONNECTIONS & INSPECTOR */}
                    {/* ========================================================= */}
                    {activeTab === 'connections' && (

                        <div className="h-full flex flex-col gap-4">
                            {/* Top Banner: Stats and New Credential */}
                            <div className="flex items-center justify-between bg-slate-950/40 p-4 rounded-xl border border-slate-800">
                                <div>
                                    <h3 className="text-sm font-bold text-white flex items-center gap-2">
                                        <span>Data Containers & Physical Connections</span>
                                        <span className="text-[11px] font-normal text-slate-400">(`Infrastructure.DataContainers` & `Vault.CredentialBindings`)</span>
                                    </h3>
                                    <div className="flex items-center gap-2 text-xs text-slate-400 mt-1">
                                        <span>Total Containers: <strong className="text-white font-mono">{connections.length}</strong></span>
                                        <span>•</span>
                                        <span className="text-emerald-400 flex items-center gap-1">
                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                                            Configured: <strong className="font-mono">{connections.filter(c => c.isConfigured).length}</strong>
                                        </span>
                                        <span>•</span>
                                        <span className="text-amber-400 flex items-center gap-1">
                                            <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
                                            Uncredentialed: <strong className="font-mono">{connections.filter(c => !c.isConfigured).length}</strong>
                                        </span>
                                    </div>
                                </div>
                                <button
                                    onClick={() => {
                                        setActiveInspectConnection(null);
                                        setInspectResult(null);
                                        setEditingConnection(null);
                                        setBindModalContainer(null);
                                        setIsCreatingConnection(true);
                                    }}
                                    className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-semibold shadow-sm transition-all"
                                >
                                    <span className="material-symbols-outlined text-[16px]">add</span>
                                    New Connection
                                </button>
                            </div>

                            {/* Reorderable Multi-Level Grouping Bar */}
                            <div className="flex flex-wrap items-center justify-between gap-2 bg-slate-950/60 p-2.5 rounded-xl border border-slate-800 text-xs">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-slate-400 font-semibold flex items-center gap-1">
                                        <span className="material-symbols-outlined text-[16px]">account_tree</span>
                                        Group By:
                                    </span>
                                    {activeGrouping.length === 0 ? (
                                        <span className="text-slate-500 italic">None (Flat List)</span>
                                    ) : (
                                        activeGrouping.map((key, idx) => {
                                            const dim = GROUP_DIMENSIONS.find(d => d.key === key);
                                            return (
                                                <div 
                                                    key={key} 
                                                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-950/80 border border-emerald-700/60 text-emerald-300 text-xs font-semibold shadow-sm animate-fade-in"
                                                >
                                                    <button
                                                        type="button"
                                                        onClick={(e) => { e.stopPropagation(); moveGroupingLeft(idx); }}
                                                        disabled={idx === 0}
                                                        className={`p-0.5 rounded ${idx === 0 ? 'text-emerald-800 cursor-not-allowed' : 'hover:bg-emerald-900 text-emerald-200'}`}
                                                        title="Increase grouping priority (move left)"
                                                    >
                                                        <span className="material-symbols-outlined text-[13px]">chevron_left</span>
                                                    </button>
                                                    <span className="material-symbols-outlined text-[14px]">{dim?.icon || 'label'}</span>
                                                    <span>{dim?.label || key}</span>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => { e.stopPropagation(); moveGroupingRight(idx); }}
                                                        disabled={idx === activeGrouping.length - 1}
                                                        className={`p-0.5 rounded ${idx === activeGrouping.length - 1 ? 'text-emerald-800 cursor-not-allowed' : 'hover:bg-emerald-900 text-emerald-200'}`}
                                                        title="Decrease grouping priority (move right)"
                                                    >
                                                        <span className="material-symbols-outlined text-[13px]">chevron_right</span>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => { e.stopPropagation(); removeGrouping(key); }}
                                                        className="p-0.5 rounded hover:bg-emerald-900 text-emerald-400 hover:text-white ml-0.5"
                                                        title="Remove grouping dimension"
                                                    >
                                                        <span className="material-symbols-outlined text-[13px]">close</span>
                                                    </button>
                                                </div>
                                            );
                                        })
                                    )}

                                    {/* Inactive dimensions to add */}
                                    {GROUP_DIMENSIONS.filter(d => !activeGrouping.includes(d.key)).map(d => (
                                        <button
                                            key={d.key}
                                            type="button"
                                            onClick={() => addGrouping(d.key)}
                                            className="px-2 py-1 rounded-lg bg-slate-900 hover:bg-slate-800 border border-slate-800 hover:border-slate-700 text-slate-400 hover:text-white text-xs flex items-center gap-1 transition-all"
                                            title={`Group by ${d.label}`}
                                        >
                                            <span className="material-symbols-outlined text-[13px]">add</span>
                                            {d.label}
                                        </button>
                                    ))}
                                </div>

                                <div className="flex items-center gap-2">
                                    {activeGrouping.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => { setActiveGrouping([]); setExpandedGroups({}); }}
                                            className="px-2.5 py-1 text-xs text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors flex items-center gap-1"
                                            title="Clear all groupings to view flat list"
                                        >
                                            <span className="material-symbols-outlined text-[14px]">view_list</span>
                                            Flat List
                                        </button>
                                    )}
                                    {activeGrouping.length > 0 && (() => {
                                        const anyExpanded = expandedGroups['__all'] === true || Object.values(expandedGroups).some(Boolean);
                                        return (
                                            <button
                                                type="button"
                                                onClick={() => setExpandedGroups(prev => (anyExpanded ? {} : { __all: true }))}
                                                className="px-2.5 py-1 text-xs text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors flex items-center gap-1"
                                                title={anyExpanded ? 'Collapse all groups' : 'Expand all groups'}
                                            >
                                                <span className="material-symbols-outlined text-[14px]">
                                                    {anyExpanded ? 'unfold_less' : 'unfold_more'}
                                                </span>
                                                {anyExpanded ? 'Collapse All' : 'Expand All'}
                                            </button>
                                        );
                                    })()}
                                </div>
                            </div>

                            <div className="flex-1 grid grid-cols-12 gap-6 overflow-hidden">
                                {/* Connections Table (takes col-span-7 when side panel is open, otherwise col-span-12) */}
                                <div className={`${activeInspectConnection ? 'col-span-7' : 'col-span-12'} overflow-y-auto border border-slate-800 rounded-xl bg-slate-950/40 transition-all`}>
                                    <table className="w-full text-left text-xs text-slate-300">
                                        <thead className="bg-slate-900 text-slate-400 sticky top-0 border-b border-slate-800 z-10">
                                            <tr>
                                                <th className="py-2.5 px-3">Target Container</th>
                                                <th className="py-2.5 px-3">Credential Status & Binding</th>
                                                <th className="py-2.5 px-3">Application</th>
                                                <th className="py-2.5 px-3">Host / Endpoint</th>
                                                <th className="py-2.5 px-3 text-right">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-800/60 font-mono">
                                            {(() => {
                                                const renderContainerRow = (c, depth = 0) => {
                                                    const isConfigured = c.isConfigured;
                                                    const isCredOAuth = c.isOAuth || c.AuthModelID === 2 || c.SourceTypeID === 2;
                                                    const isCredRest = c.isRest || c.AuthModelID === 1 || c.SourceTypeID === 7;
                                                    const isEditing = isCreatingConnection && (editingConnection ? editingConnection.ContainerID === c.ContainerID : bindModalContainer?.ContainerID === c.ContainerID);
                                                    const isInspecting = activeInspectConnection?.ContainerID === c.ContainerID;

                                                    return (
                                                        <tr 
                                                            key={`${c.ContainerID}-${c.BindingID || 'none'}`} 
                                                            className={`hover:bg-slate-800/40 border-l-4 ${
                                                                isEditing ? 'bg-emerald-950/50 border-l-emerald-500 shadow-sm' : 
                                                                isInspecting ? 'bg-teal-950/50 border-l-teal-500 shadow-sm' : 
                                                                'border-l-transparent'
                                                            } transition-all`}
                                                        >
                                                            {/* Container & Type */}
                                                            <td className="py-2.5 px-3" style={{ paddingLeft: `${12 + depth * 16}px` }}>
                                                                <div className="font-semibold text-white flex items-center gap-1.5">
                                                                    <span className="material-symbols-outlined text-[15px] text-slate-400">
                                                                        {c.ContainerType === 'SAAS_TENANT' ? 'cloud' : 'database'}
                                                                    </span>
                                                                    <span>{c.ContainerName}</span>
                                                                    {isEditing && (
                                                                        <span className="px-1.5 py-0.5 rounded text-[9px] bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 font-sans font-bold uppercase tracking-wider">
                                                                            Editing
                                                                        </span>
                                                                    )}
                                                                    {isInspecting && (
                                                                        <span className="px-1.5 py-0.5 rounded text-[9px] bg-teal-500/20 text-teal-300 border border-teal-500/40 font-sans font-bold uppercase tracking-wider">
                                                                            Inspecting
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="text-[10px] text-slate-500 font-sans mt-0.5 flex items-center gap-1">
                                                                    <span>{c.ContainerType || 'RELATIONAL_DATABASE'}</span>
                                                                    <span>•</span>
                                                                    <span>Container #{c.ContainerID}</span>
                                                                </div>
                                                            </td>

                                                            {/* Credential Status & Binding */}
                                                            <td className="py-2.5 px-3">
                                                                {isConfigured ? (
                                                                    <div>
                                                                        <div className="flex items-center gap-1.5">
                                                                            <span className="px-1.5 py-0.5 rounded text-[10px] font-sans font-bold bg-emerald-950/90 text-emerald-300 border border-emerald-700/70 flex items-center gap-1">
                                                                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                                                                                CONFIGURED
                                                                            </span>
                                                                            <span className="text-white text-xs font-semibold">
                                                                                {c.CredentialName || c.BindingName || `Credential #${c.CredentialID}`}
                                                                            </span>
                                                                        </div>
                                                                        <div className="text-[10px] text-slate-400 font-sans mt-0.5 flex items-center gap-1">
                                                                            <span className="text-slate-500">Auth:</span> {c.AuthModelName || c.AuthModelCode || 'SQL Login'}
                                                                            {c.BindingName && c.BindingName !== c.CredentialName && (
                                                                                <span className="text-slate-500 italic">({c.BindingName})</span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="px-1.5 py-0.5 rounded text-[10px] font-sans font-bold bg-amber-950/80 text-amber-300 border border-amber-700/60 flex items-center gap-1">
                                                                            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"></span>
                                                                            UNCREDENTIALED
                                                                        </span>
                                                                        <button
                                                                            onClick={() => {
                                                                                setActiveInspectConnection(null);
                                                                                setInspectResult(null);
                                                                                setEditingConnection(null);
                                                                                setBindModalContainer(c);
                                                                                setIsCreatingConnection(true);
                                                                            }}
                                                                            className="px-2 py-0.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 rounded border border-amber-500/40 text-[11px] font-sans font-medium flex items-center gap-1 transition-all"
                                                                        >
                                                                            <span className="material-symbols-outlined text-[13px]">add_link</span>
                                                                            Bind Credential
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </td>

                                                            {/* App & Version */}
                                                            <td className="py-2.5 px-3">
                                                                <div className="flex items-center gap-1.5">
                                                                    <span className="px-2 py-0.5 rounded text-[10px] bg-slate-800 text-slate-300 border border-slate-700 font-sans font-semibold">
                                                                        {c.SourceAppCode || 'PASSPORT'}
                                                                    </span>
                                                                    {c.AppVersion ? (
                                                                        <span className="text-amber-400 font-mono font-bold text-xs">v{c.AppVersion}</span>
                                                                    ) : (
                                                                        <span className="text-slate-600 text-xs italic font-sans">—</span>
                                                                    )}
                                                                </div>
                                                                <div className="text-[10px] text-slate-500 font-sans mt-0.5">
                                                                    {c.SourceTypeName || c.SourceTypeCode || 'MSSQL'}
                                                                </div>
                                                            </td>

                                                            {/* Host / Endpoint */}
                                                            <td className="py-2.5 px-3 text-[11px]">
                                                                <div className="font-bold text-slate-200">
                                                                    {isCredOAuth ? (
                                                                        <span className="text-sky-300 font-mono">Realm: {c.DatabaseName || c.ContainerName || 'Sandbox'}</span>
                                                                    ) : (
                                                                        <>
                                                                            {c.effectiveIp || c.HostServer || c.HostName ? `${c.effectiveIp || c.HostServer || c.HostName}:${c.HostPort || c.ListenPort || 1433}` : '127.0.0.1:1433'}
                                                                        </>
                                                                    )}
                                                                </div>
                                                                <div className="text-[10px] text-slate-400 font-sans truncate max-w-[180px]">
                                                                    {c.HostDisplayName || c.DeviceName || 'Default Host'}
                                                                </div>
                                                            </td>

                                                            {/* Actions */}
                                                            <td className="py-2.5 px-3 text-right">
                                                                <div className="flex items-center justify-end gap-1.5">
                                                                    {isConfigured ? (
                                                                        <>
                                                                            {/* Test button */}
                                                                            <button
                                                                                onClick={() => handleTestConnection(c.ContainerID || c.CredentialID, null, Boolean(c.ContainerID))}
                                                                                disabled={testingRowId === (c.ContainerID || c.CredentialID)}
                                                                                className="px-2 py-1 bg-sky-600/20 hover:bg-sky-600/40 text-sky-300 rounded border border-sky-500/30 text-[11px] flex items-center gap-1 font-sans"
                                                                                title="Test connection reachability"
                                                                            >
                                                                                <span className={`material-symbols-outlined text-[13px] ${testingRowId === (c.ContainerID || c.CredentialID) ? 'animate-spin' : ''}`}>
                                                                                    {testingRowId === (c.ContainerID || c.CredentialID) ? 'refresh' : (isCredOAuth ? 'sync' : 'wifi')}
                                                                                </span>
                                                                                Test
                                                                            </button>

                                                                            {/* Inspect button */}
                                                                            {(c.isSqlLogin || isCredOAuth) ? (
                                                                                <button
                                                                                    onClick={() => handleInspectConnection(c)}
                                                                                    disabled={inspectLoading && (activeInspectConnection?.ContainerID === c.ContainerID || activeInspectConnection?.CredentialID === c.CredentialID)}
                                                                                    className={`px-2 py-1 rounded border text-[11px] flex items-center gap-1 font-sans transition-all ${
                                                                                        isInspecting 
                                                                                            ? 'bg-teal-600 text-white border-teal-500 font-semibold' 
                                                                                            : 'bg-teal-600/20 hover:bg-teal-600/40 text-teal-300 border-teal-500/30'
                                                                                    }`}
                                                                                    title="Introspect live schema against Catalog"
                                                                                >
                                                                                    <span className={`material-symbols-outlined text-[13px] ${inspectLoading && (activeInspectConnection?.ContainerID === c.ContainerID || activeInspectConnection?.CredentialID === c.CredentialID) ? 'animate-spin' : ''}`}>
                                                                                        search
                                                                                    </span>
                                                                                    Inspect
                                                                                </button>
                                                                            ) : null}



                                                                            {/* Edit Credential */}
                                                                            <button
                                                                                onClick={() => {
                                                                                    setActiveInspectConnection(null);
                                                                                    setInspectResult(null);
                                                                                    setEditingConnection(c);
                                                                                    setBindModalContainer(null);
                                                                                    setIsCreatingConnection(true);
                                                                                }}
                                                                                className={`p-1 rounded ${isEditing ? 'text-emerald-400 bg-emerald-950/60' : 'hover:text-white text-slate-400'}`}
                                                                                title="Edit Credential & Binding"
                                                                            >
                                                                                <span className="material-symbols-outlined text-[15px]">edit</span>
                                                                            </button>

                                                                            {/* Unbind Credential */}
                                                                            {c.BindingID && (
                                                                                <button
                                                                                    onClick={() => handleUnbindCredential(c.BindingID)}
                                                                                    className="p-1 hover:text-red-400 text-slate-500"
                                                                                    title="Unbind Credential from Container"
                                                                                >
                                                                                    <span className="material-symbols-outlined text-[15px]">link_off</span>
                                                                                </button>
                                                                            )}
                                                                        </>
                                                                    ) : (
                                                                        <button
                                                                            onClick={() => {
                                                                                setActiveInspectConnection(null);
                                                                                setInspectResult(null);
                                                                                setEditingConnection(null);
                                                                                setBindModalContainer(c);
                                                                                setIsCreatingConnection(true);
                                                                            }}
                                                                            className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded text-xs font-semibold flex items-center gap-1 font-sans shadow-sm"
                                                                        >
                                                                            <span className="material-symbols-outlined text-[14px]">add_link</span>
                                                                            Bind Credential
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                };

                                                if (activeGrouping.length === 0) {
                                                    return connections.map(c => renderContainerRow(c, 0));
                                                }

                                                // Recursive hierarchical group rendering
                                                const renderGroupLevel = (items, depth = 0, parentPath = '') => {
                                                    if (depth >= activeGrouping.length) {
                                                        return items.map(c => renderContainerRow(c, depth));
                                                    }

                                                    const key = activeGrouping[depth];
                                                    const dim = GROUP_DIMENSIONS.find(d => d.key === key);
                                                    const map = new Map();

                                                    for (const item of items) {
                                                        const val = item[key] || `Unassigned ${dim?.label || key}`;
                                                        if (!map.has(val)) map.set(val, []);
                                                        map.get(val).push(item);
                                                    }

                                                    const sortedKeys = Array.from(map.keys()).sort((a, b) => String(a).localeCompare(String(b)));

                                                    return sortedKeys.map(groupVal => {
                                                        const groupItems = map.get(groupVal);
                                                        const path = parentPath ? `${parentPath} > ${groupVal}` : groupVal;
                                                        const isExpanded = expandedGroups['__all'] === true ? (expandedGroups[path] !== false) : !!expandedGroups[path];
                                                        const isCollapsed = !isExpanded;
                                                        const grpConfigured = groupItems.filter(i => i.isConfigured).length;
                                                        const grpUncred = groupItems.length - grpConfigured;

                                                        return (
                                                            <React.Fragment key={path}>
                                                                <tr 
                                                                    onClick={() => toggleGroupCollapse(path)}
                                                                    className="bg-slate-900/90 hover:bg-slate-800/80 cursor-pointer select-none border-t border-b border-slate-800 transition-colors"
                                                                >
                                                                    <td colSpan={5} className="py-2 px-3">
                                                                        <div className="flex items-center justify-between" style={{ paddingLeft: `${depth * 18}px` }}>
                                                                            <div className="flex items-center gap-2">
                                                                                <span className={`material-symbols-outlined text-[16px] text-slate-400 transition-transform ${isCollapsed ? '-rotate-90' : ''}`}>
                                                                                    expand_more
                                                                                </span>
                                                                                <span className="material-symbols-outlined text-[16px] text-emerald-400">
                                                                                    {dim?.icon || 'folder'}
                                                                                </span>
                                                                                <span className="text-slate-400 text-xs font-sans font-medium">
                                                                                    {dim?.label}:
                                                                                </span>
                                                                                <span className="text-white text-xs font-bold font-sans">
                                                                                    {groupVal}
                                                                                </span>
                                                                                <span className="text-slate-500 text-[10px] font-sans font-normal ml-1">
                                                                                    ({groupItems.length} {groupItems.length === 1 ? 'container' : 'containers'})
                                                                                </span>
                                                                            </div>
                                                                            <div className="flex items-center gap-1.5">
                                                                                {grpConfigured > 0 && (
                                                                                    <span className="px-1.5 py-0.5 rounded text-[9px] bg-emerald-950/80 text-emerald-300 border border-emerald-800/60 font-sans font-medium">
                                                                                        {grpConfigured} Configured
                                                                                    </span>
                                                                                )}
                                                                                {grpUncred > 0 && (
                                                                                    <span className="px-1.5 py-0.5 rounded text-[9px] bg-amber-950/80 text-amber-300 border border-amber-800/60 font-sans font-medium">
                                                                                        {grpUncred} Uncredentialed
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                                {!isCollapsed && renderGroupLevel(groupItems, depth + 1, path)}
                                                            </React.Fragment>
                                                        );
                                                    });
                                                };

                                                return renderGroupLevel(connections, 0, '');
                                            })()}
                                        </tbody>
                                    </table>
                                </div>

                                {/* SIDE PANEL 2: Live Inspector Panel */}
                                {activeInspectConnection && (
                                    <div className="col-span-5 border border-slate-800 rounded-xl bg-slate-950 p-4 flex flex-col gap-3 overflow-hidden animate-fade-in shadow-xl">
                                        {/* Header */}
                                        <div className="flex items-center justify-between pb-2 border-b border-slate-800">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                                                    inspectLoading ? 'bg-amber-400 animate-pulse' :
                                                    (inspectResult?.status === 'CONNECTED' ? 'bg-emerald-400' : 'bg-red-400')
                                                }`} />
                                                <h4 className="text-xs font-bold text-white uppercase tracking-wider truncate">
                                                    Live Inspector: {activeInspectConnection.ConnectionName}
                                                </h4>
                                            </div>
                                            <button
                                                onClick={() => {
                                                    setActiveInspectConnection(null);
                                                    setInspectResult(null);
                                                }}
                                                className="text-slate-400 hover:text-white flex-shrink-0"
                                            >
                                                <span className="material-symbols-outlined text-[18px]">close</span>
                                            </button>
                                        </div>

                                        {/* Top Scope Configuration: App & Version Assignment */}
                                        <div className="bg-slate-900/90 p-3 rounded-lg border border-slate-800 flex flex-col gap-2">
                                            <div className="text-[11px] font-semibold text-slate-300 flex items-center justify-between">
                                                <span className="flex items-center gap-1">
                                                    <span className="material-symbols-outlined text-[15px] text-teal-400">tune</span>
                                                    Scope: Application & Version
                                                </span>
                                                <button
                                                    onClick={handleSaveInspectAppVersion}
                                                    disabled={updatingAppVersion || inspectLoading}
                                                    className="px-2 py-0.5 bg-teal-600 hover:bg-teal-500 text-white rounded text-[10px] font-semibold flex items-center gap-1 transition-all"
                                                >
                                                    <span className={`material-symbols-outlined text-[12px] ${updatingAppVersion ? 'animate-spin' : ''}`}>
                                                        {updatingAppVersion ? 'refresh' : 'sync'}
                                                    </span>
                                                    Update Scope
                                                </button>
                                            </div>
                                            <div className="grid grid-cols-5 gap-2 items-center">
                                                <div className="col-span-3">
                                                    <SearchableSelect
                                                        options={appOptions}
                                                        value={inspectAppId}
                                                        onChange={handleInspectAppChange}
                                                        placeholder="Select Application..."
                                                        className="w-full"
                                                    />
                                                </div>
                                                <div className="col-span-2">
                                                    <SearchableSelect
                                                        options={inspectVersionOptions}
                                                        value={inspectVersion || ''}
                                                        onChange={handleInspectVersionChange}
                                                        allowCustom={true}
                                                        customPrompt="Add / Use version"
                                                        placeholder="App Version..."
                                                        className="w-full"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Loading Skeleton Indicator */}
                                        {inspectLoading && (
                                            <div className="flex-1 flex flex-col items-center justify-center gap-3 p-6 text-slate-400 animate-fade-in">
                                                <span className="material-symbols-outlined text-3xl animate-spin text-teal-400">search</span>
                                                <div className="text-xs text-center">
                                                    <div className="font-semibold text-white">Introspecting live database schema...</div>
                                                    <div className="text-[11px] text-slate-500 mt-1 font-mono">
                                                        {activeInspectConnection.HostServer || activeInspectConnection.effectiveIp}:{activeInspectConnection.HostPort || 1433} / {activeInspectConnection.DatabaseName || 'master'}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Results Content */}
                                        {!inspectLoading && inspectResult && (
                                            <div className="flex-1 flex flex-col gap-3 overflow-hidden">
                                                <div className="grid grid-cols-4 gap-2 text-center text-xs">
                                                    <div className="bg-slate-900 p-2 rounded-lg border border-slate-800 flex flex-col justify-between">
                                                        <div className="text-slate-400 text-[10px] font-medium">Live Database</div>
                                                        <div className="font-bold text-emerald-400 text-sm">{inspectResult.totalLiveTables || 0} Tables</div>
                                                        <div className="text-[10px] text-slate-500">{(inspectResult.totalLiveColumns || 0).toLocaleString()} cols</div>
                                                    </div>
                                                    <div className="bg-slate-900 p-2 rounded-lg border border-slate-800 flex flex-col justify-between">
                                                        <div className="text-slate-400 text-[10px] font-medium" title="Cataloged Entities (Tables) & Field Definitions">Catalog Defs</div>
                                                        <div className="font-bold text-indigo-400 text-sm">{inspectResult.catalogEntitiesCount || inspectResult.catalogDefinitionsCount || 0} Tables</div>
                                                        <div className="text-[10px] text-indigo-400/80">{(inspectResult.catalogFieldsCount || 0).toLocaleString()} fields</div>
                                                    </div>
                                                    <div className="bg-slate-900 p-2 rounded-lg border border-slate-800 flex flex-col justify-between">
                                                        <div className="text-slate-400 text-[10px] font-medium">Unmapped Tables</div>
                                                        <div className="font-bold text-amber-400 text-sm">{inspectResult.unmappedTables?.length || inspectResult.unmappedTablesCount || inspectResult.unmappedTableCount || 0} Tables</div>
                                                        <div className="text-[10px] text-amber-400/80">
                                                            {(inspectResult.unmappedTables || []).filter(t => !t.inCatalog).length} new, {(inspectResult.unmappedTables || []).filter(t => t.inCatalog).length} partial
                                                        </div>
                                                    </div>
                                                    <div className="bg-slate-900 p-2 rounded-lg border border-slate-800 flex flex-col justify-between">
                                                        <div className="text-slate-400 text-[10px] font-medium">Schema Drift</div>
                                                        <div className="font-bold text-cyan-400 text-sm">{inspectResult.tableDiffs?.length || 0} Diffs</div>
                                                        <div className="text-[10px] text-slate-500">
                                                            {(inspectResult.tableDiffs?.length || 0) === 0 ? '100% In Sync' : `${inspectResult.missingColumnsCount || 0} cols missing`}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Error State */}
                                                {inspectResult.error && (
                                                    <div className="p-3 bg-red-950/40 border border-red-800/60 rounded-lg text-xs text-red-300">
                                                        {inspectResult.error}
                                                    </div>
                                                )}

                                                {/* All Tables Cataloged Clean State */}
                                                {(!inspectResult.unmappedTables || inspectResult.unmappedTables.length === 0) && !inspectResult.error && (
                                                    <div className="flex-1 flex flex-col items-center justify-center gap-2 p-6 text-slate-400 border border-slate-800 rounded-lg bg-slate-900/40">
                                                        <span className="material-symbols-outlined text-3xl text-emerald-400">check_circle</span>
                                                        <div className="text-xs font-semibold text-white">All Live Tables Cataloged!</div>
                                                        <div className="text-[11px] text-slate-400 text-center max-w-xs">
                                                            All {inspectResult.totalLiveTables || 0} tables in this database are fully mapped to Catalog Entity Definitions with no unmapped tables detected.
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Unmapped Tables Multi-Select and Import Controls */}
                                                {inspectResult.unmappedTables && inspectResult.unmappedTables.length > 0 && (
                                                    <div className="flex-1 overflow-hidden border border-slate-800 rounded-lg p-2.5 bg-slate-900/60 flex flex-col gap-2">
                                                        <div className="flex items-center justify-between pb-1.5 border-b border-slate-800 flex-shrink-0">
                                                            <div className="text-[11px] font-semibold text-amber-300 flex items-center gap-1">
                                                                <span className="material-symbols-outlined text-[15px]">warning</span>
                                                                Unmapped Live Tables ({inspectResult.unmappedTables.length}):
                                                            </div>
                                                            <div className="flex gap-1.5 text-[10px]">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        const allNames = inspectResult.unmappedTables.map(t => typeof t === 'string' ? t : t.physicalTable);
                                                                        setSelectedImportTables(allNames);
                                                                    }}
                                                                    className="text-teal-400 hover:underline"
                                                                >
                                                                    Select All
                                                                </button>
                                                                <span className="text-slate-600">|</span>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setSelectedImportTables([])}
                                                                    className="text-slate-400 hover:underline"
                                                                >
                                                                    Deselect
                                                                </button>
                                                            </div>
                                                        </div>

                                                        {/* Scrollable List of Tables with Diff Expansion */}
                                                        <div className="flex-1 space-y-1.5 overflow-y-auto pr-1">
                                                            {inspectResult.unmappedTables.map((tbl) => {
                                                                const physicalName = typeof tbl === 'string' ? tbl : tbl.physicalTable;
                                                                const abstractName = typeof tbl === 'string' ? tbl : tbl.abstractTable;
                                                                const isChecked = selectedImportTables.includes(physicalName);
                                                                const isExpanded = expandedTableDiff === physicalName;
                                                                const cols = tbl.columns || [];

                                                                return (
                                                                    <div key={physicalName} className="border border-slate-800 rounded-lg bg-slate-950/80 overflow-hidden">
                                                                        <div className="flex items-center justify-between p-2 hover:bg-slate-900/60 transition-colors">
                                                                            <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={isChecked}
                                                                                    onChange={() => {
                                                                                        if (isChecked) {
                                                                                            setSelectedImportTables(selectedImportTables.filter(t => t !== physicalName));
                                                                                        } else {
                                                                                            setSelectedImportTables([...selectedImportTables, physicalName]);
                                                                                        }
                                                                                    }}
                                                                                    className="rounded border-slate-700 text-teal-500 focus:ring-teal-400"
                                                                                />
                                                                                <div className="truncate">
                                                                                    <span className="font-mono font-semibold text-white text-xs">{physicalName}</span>
                                                                                    {abstractName && abstractName !== physicalName && (
                                                                                        <span className="text-slate-400 text-[10px] ml-1.5 font-sans">
                                                                                            &rarr; {abstractName}
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                            </label>

                                                                            <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
                                                                                {tbl.inCatalog ? (
                                                                                    <span className="px-1.5 py-0.5 rounded text-[9px] bg-amber-950/60 text-amber-300 border border-amber-800/60 font-sans font-medium">
                                                                                        Partial ({tbl.diffCount} diffs)
                                                                                    </span>
                                                                                ) : (
                                                                                    <span className="px-1.5 py-0.5 rounded text-[9px] bg-emerald-950/60 text-emerald-300 border border-emerald-800/60 font-sans font-medium">
                                                                                        New ({tbl.liveColumnCount || cols.length} cols)
                                                                                    </span>
                                                                                )}
                                                                                {cols.length > 0 && (
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => setExpandedTableDiff(isExpanded ? null : physicalName)}
                                                                                        className="text-slate-400 hover:text-white p-0.5"
                                                                                        title="View column diffs"
                                                                                    >
                                                                                        <span className={`material-symbols-outlined text-[16px] transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                                                                                            expand_more
                                                                                        </span>
                                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                        </div>

                                                                        {/* Expanded Column Diff Table */}
                                                                        {isExpanded && cols.length > 0 && (
                                                                            <div className="border-t border-slate-800 bg-slate-900/95 p-2 text-[10px] max-h-56 overflow-y-auto">
                                                                                <table className="w-full text-left">
                                                                                    <thead>
                                                                                        <tr className="text-slate-500 border-b border-slate-800">
                                                                                            <th className="pb-1" title="Column Name in live database">Column</th>
                                                                                            <th className="pb-1" title="Data type in live SQL Server">Live Type</th>
                                                                                            <th className="pb-1" title="Data type currently in Catalog definitions">Catalog Def</th>
                                                                                            <th className="pb-1 text-right" title="Drift Status: New column, Modified type, or Match">Status</th>
                                                                                        </tr>
                                                                                    </thead>
                                                                                    <tbody className="divide-y divide-slate-800/40 font-mono">
                                                                                        {cols.map((c) => {
                                                                                            const colName = c.columnName || c.ColumnName;
                                                                                            const liveType = (c.liveDataType || c.DataType) + ((c.liveLength || c.MaxLength) ? `(${c.liveLength || c.MaxLength})` : '');
                                                                                            const catType = (c.catalogDataType || c.CatalogDataType) 
                                                                                                ? `${c.catalogDataType || c.CatalogDataType}${(c.catalogLength || c.CatalogLength) ? `(${c.catalogLength || c.CatalogLength})` : ''}` 
                                                                                                : '—';
                                                                                            const statusType = c.diffType || (c.status === 'New' ? 'NEW_COLUMN' : (c.status === 'Diff' ? 'MODIFIED' : 'MATCH'));

                                                                                            return (
                                                                                                <tr key={colName} className="hover:bg-slate-800/30">
                                                                                                    <td className="py-1 text-slate-200 font-semibold">{colName}</td>
                                                                                                    <td className="py-1 text-teal-300">{liveType}</td>
                                                                                                    <td className="py-1 text-slate-400">{catType}</td>
                                                                                                    <td className="py-1 text-right">
                                                                                                        {statusType === 'NEW_COLUMN' && (
                                                                                                            <span className="px-1.5 py-0.5 rounded text-[9px] bg-emerald-950/70 text-emerald-300 border border-emerald-800/60 font-sans font-semibold">New</span>
                                                                                                        )}
                                                                                                        {statusType === 'MODIFIED' && (
                                                                                                            <span className="px-1.5 py-0.5 rounded text-[9px] bg-amber-950/70 text-amber-300 border border-amber-800/60 font-sans font-semibold">Diff</span>
                                                                                                        )}
                                                                                                        {statusType === 'MATCH' && (
                                                                                                            <span className="px-1.5 py-0.5 rounded text-[9px] bg-slate-800/70 text-slate-400 border border-slate-700/60 font-sans">Match</span>
                                                                                                        )}
                                                                                                    </td>
                                                                                                </tr>
                                                                                            );
                                                                                        })}
                                                                                    </tbody>
                                                                                </table>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>

                                                        {/* Import Selected Button Footer */}
                                                        <div className="pt-2 border-t border-slate-800 flex items-center justify-between flex-shrink-0">
                                                            <span className="text-[11px] text-slate-400 font-sans">
                                                                {selectedImportTables.length} table(s) selected
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={handleImportSelectedTables}
                                                                disabled={selectedImportTables.length === 0 || importingTables}
                                                                className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 shadow-md transition-all ${
                                                                    selectedImportTables.length > 0 && !importingTables
                                                                        ? 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white cursor-pointer'
                                                                        : 'bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-700'
                                                                }`}
                                                            >
                                                                <span className={`material-symbols-outlined text-[15px] ${importingTables ? 'animate-spin' : ''}`}>
                                                                    {importingTables ? 'refresh' : 'add_circle'}
                                                                </span>
                                                                Import Selected to Catalog
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Modern Recursive Connection & Binding Drawer */}
                            <ConnectionBindingDrawer
                                isOpen={isCreatingConnection}
                                onClose={() => {
                                    setIsCreatingConnection(false);
                                    setEditingConnection(null);
                                    setBindModalContainer(null);
                                }}
                                editingConnection={editingConnection}
                                targetContainer={bindModalContainer}
                                onSaveSuccess={() => {
                                    loadConnections();
                                }}
                                authHeaders={authHeaders}
                                apps={apps}
                                driverTypes={driverTypes}
                                authModels={authModels}
                                integrationAccounts={integrationAccounts}
                                onNotification={showNotify}
                            />
                        </div>
                    )}

                    {/* ========================================================= */}
                    {/* TAB 2: TRANSFORMS & REGEX SIMULATOR */}
                    {/* ========================================================= */}
                    {activeTab === 'transforms' && (
                        <div className="h-full grid grid-cols-12 gap-6 overflow-hidden">
                            {/* Left Pane: Transforms Table */}
                            <div className="col-span-6 flex flex-col gap-3 overflow-hidden">
                                <div className="flex items-center justify-between bg-slate-950/40 p-3 rounded-xl border border-slate-800">
                                    <div>
                                        <h3 className="text-xs font-bold text-white uppercase tracking-wider">Scoped Instance Transforms</h3>
                                        <p className="text-[11px] text-slate-400">
                                            Regex mappings from physical database/table strings to catalog standard entities.
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleResetSimulator}
                                        className="flex items-center gap-1 px-2.5 py-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-semibold shadow-sm transition-all"
                                    >
                                        <span className="material-symbols-outlined text-[15px]">add</span>
                                        New Rule
                                    </button>
                                </div>

                                <div className="flex-1 overflow-y-auto border border-slate-800 rounded-xl bg-slate-950/40">
                                    <table className="w-full text-left text-xs text-slate-300">
                                        <thead className="bg-slate-900 text-slate-400 sticky top-0 border-b border-slate-800">
                                            <tr>
                                                <th className="py-2.5 px-3">Scope</th>
                                                <th className="py-2.5 px-3">App / Ver</th>
                                                <th className="py-2.5 px-3">Pattern &rarr; Replacement</th>
                                                <th className="py-2.5 px-2 text-center">Pri</th>
                                                <th className="py-2.5 px-3 text-right">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-800/60 font-mono">
                                            {transforms.map((t) => {
                                                const isCurrent = editingTransform?.TransformID === t.TransformID;
                                                const appObj = apps.find(a => a.SourceAppID === t.SourceAppID);
                                                const appLabel = appObj?.AppName || t.AppName || t.AppCode || `App #${t.SourceAppID}`;
                                                return (
                                                    <tr
                                                        key={t.TransformID}
                                                        onClick={() => handleSelectTransform(t)}
                                                        className={`cursor-pointer transition-colors ${
                                                            isCurrent
                                                                ? 'bg-emerald-950/50 border-l-4 border-emerald-500 text-white'
                                                                : 'hover:bg-slate-800/40'
                                                        }`}
                                                    >
                                                        <td className="py-2 px-3">
                                                            <span className={`px-2 py-0.5 rounded text-[9px] font-sans font-bold ${
                                                                t.TransformScope === 'DATABASE'
                                                                    ? 'bg-indigo-950 text-indigo-300 border border-indigo-700'
                                                                    : 'bg-amber-950 text-amber-300 border border-amber-700'
                                                            }`}>
                                                                {t.TransformScope}
                                                            </span>
                                                        </td>
                                                        <td className="py-2 px-3 font-sans text-xs">
                                                            <div className="font-semibold text-slate-200 truncate max-w-[110px]" title={appLabel}>
                                                                {appLabel}
                                                            </div>
                                                            <div className="text-[10px] text-amber-400 font-mono">
                                                                {t.AppVersion ? `v${t.AppVersion}` : '* (All Versions)'}
                                                            </div>
                                                        </td>
                                                        <td className="py-2 px-3">
                                                            <div className="text-emerald-400 font-bold truncate max-w-[180px]" title={t.SearchPattern}>
                                                                {t.SearchPattern}
                                                            </div>
                                                            <div className="text-sky-400 text-[11px] truncate max-w-[180px]" title={t.ReplacementPattern}>
                                                                &rarr; {t.ReplacementPattern}
                                                            </div>
                                                        </td>
                                                        <td className="py-2 px-2 text-center text-slate-400 font-sans text-[11px]">
                                                            {t.PriorityOrder}
                                                        </td>
                                                        <td className="py-2 px-3 text-right">
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDeleteTransform(t.TransformID);
                                                                }}
                                                                className="p-1 hover:text-red-400 text-slate-500"
                                                                title="Delete Transform Rule"
                                                            >
                                                                <span className="material-symbols-outlined text-[15px]">delete</span>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Right Pane: Transform Rule Editor & Live Simulator */}
                            <div className="col-span-6 flex flex-col gap-3 overflow-y-auto">
                                <div className="bg-slate-950 p-4 rounded-xl border border-slate-800 flex flex-col gap-3 shadow-xl">
                                    <div className="flex items-center justify-between pb-2 border-b border-slate-800">
                                        <div className="flex items-center gap-2">
                                            <span className="material-symbols-outlined text-teal-400">tune</span>
                                            <h4 className="text-xs font-bold text-white uppercase tracking-wider">
                                                {editingTransform ? `Edit Transform Rule #${editingTransform.TransformID}` : 'Create Transform Rule'}
                                            </h4>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                onClick={() => setShowRegexCheatsheet(true)}
                                                className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1 font-semibold"
                                            >
                                                <span className="material-symbols-outlined text-[14px]">help</span>
                                                Cheatsheet
                                            </button>
                                            {editingTransform && (
                                                <button
                                                    type="button"
                                                    onClick={handleResetSimulator}
                                                    className="text-xs text-slate-400 hover:text-white px-2 py-0.5 rounded bg-slate-800"
                                                >
                                                    Clear / New
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Scope & Priority */}
                                    <div className="grid grid-cols-2 gap-3 text-xs">
                                        <div>
                                            <label className="text-slate-400 block mb-1 font-sans">Transform Scope:</label>
                                            <select
                                                value={simScope}
                                                onChange={(e) => setSimScope(e.target.value)}
                                                className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white font-sans"
                                            >
                                                <option value="TABLE">TABLE (e.g. CUSFIL00 &rarr; CUSFIL)</option>
                                                <option value="DATABASE">DATABASE (e.g. pbsdata00 &rarr; PASSPORT)</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="text-slate-400 block mb-1 font-sans">Priority Order (Lower runs first):</label>
                                            <input
                                                type="number"
                                                value={simPriority}
                                                onChange={(e) => setSimPriority(e.target.value)}
                                                className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white font-mono"
                                            />
                                        </div>
                                    </div>

                                    {/* Application & Version */}
                                    <div className="grid grid-cols-2 gap-3 text-xs">
                                        <div>
                                            <label className="text-slate-400 block mb-1 font-sans">Source Application:</label>
                                            <SearchableSelect
                                                options={appOptions}
                                                value={simAppId}
                                                onChange={(val) => setSimAppId(parseInt(val, 10))}
                                                placeholder="Select Application..."
                                                className="w-full"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-slate-400 block mb-1 font-sans">App Version:</label>
                                            <SearchableSelect
                                                options={simVersionOptions}
                                                value={simVersion || '*'}
                                                onChange={(val) => setSimVersion(val)}
                                                allowCustom={true}
                                                customPrompt="Add / Use version"
                                                placeholder="Select or enter version..."
                                                className="w-full"
                                            />
                                        </div>
                                    </div>

                                    {/* Description */}
                                    <div className="text-xs">
                                        <label className="text-slate-400 block mb-1 font-sans">Description / Notes:</label>
                                        <input
                                            type="text"
                                            value={simDescription}
                                            onChange={(e) => setSimDescription(e.target.value)}
                                            placeholder="e.g. Strip two-digit company suffix from Passport tables"
                                            className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-slate-200 font-sans"
                                        />
                                    </div>

                                    {/* Regex Pattern & Replacement */}
                                    <div className="space-y-2.5 text-xs font-mono">
                                        <div>
                                            <label className="text-slate-400 block mb-1 font-sans">Search Pattern (Regex):</label>
                                            <input
                                                type="text"
                                                value={simPattern}
                                                onChange={(e) => setSimPattern(e.target.value)}
                                                placeholder="^([A-Za-z_]+)\d{2}$"
                                                className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-emerald-400 font-semibold"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-slate-400 block mb-1 font-sans">Replacement Pattern (Use $1, $2 for capture groups):</label>
                                            <input
                                                type="text"
                                                value={simReplacement}
                                                onChange={(e) => setSimReplacement(e.target.value)}
                                                placeholder="$1"
                                                className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-sky-400 font-semibold"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-slate-400 block mb-1 font-sans">Interactive Live Test Input:</label>
                                            <input
                                                type="text"
                                                value={simInput}
                                                onChange={(e) => setSimInput(e.target.value)}
                                                placeholder="e.g. CUSFIL00 or pbsdata00"
                                                className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white"
                                            />
                                        </div>
                                    </div>

                                    {/* Live Simulation Output Box */}
                                    <div className="p-3 rounded-lg border border-slate-800 bg-slate-900/90 text-xs flex flex-col gap-2">
                                        <div className="flex items-center justify-between font-sans">
                                            <span className="text-slate-400 font-medium">Live Matching State:</span>
                                            {simResult?.error ? (
                                                <span className="font-bold text-red-400 font-mono text-[11px] truncate max-w-[200px]" title={simResult.error}>
                                                    Syntax Error: {simResult.error}
                                                </span>
                                            ) : (
                                                <span className={`font-bold flex items-center gap-1 ${simResult?.isMatch ? 'text-emerald-400' : 'text-slate-500'}`}>
                                                    <span className="material-symbols-outlined text-[15px]">
                                                        {simResult?.isMatch ? 'check_circle' : 'cancel'}
                                                    </span>
                                                    {simResult?.isMatch ? 'MATCH FOUND' : 'NO MATCH'}
                                                </span>
                                            )}
                                        </div>

                                        {/* Captured Groups Badges */}
                                        {simResult?.captureGroups && simResult.captureGroups.length > 0 && (
                                            <div className="flex items-center gap-1.5 flex-wrap pt-1.5 border-t border-slate-800/60 font-sans">
                                                <span className="text-slate-400 font-medium text-[11px]">Capture Groups:</span>
                                                {simResult.captureGroups.map((grp, idx) => (
                                                    <span key={idx} className="px-1.5 py-0.5 rounded bg-emerald-950/80 border border-emerald-700/60 text-emerald-300 font-mono text-[11px]" title={`Capture group $${idx + 1}`}>
                                                        ${idx + 1} = &quot;{grp}&quot;
                                                    </span>
                                                ))}
                                            </div>
                                        )}

                                        {/* Warning when $1 is used without parentheses in regex */}
                                        {simResult?.missingGroupsWarning && (
                                            <div className="p-2 rounded bg-amber-950/60 border border-amber-800/70 text-amber-300 text-[11px] font-sans flex items-start gap-1.5">
                                                <span className="material-symbols-outlined text-[16px] text-amber-400 flex-shrink-0 mt-0.5">warning</span>
                                                <div>
                                                    <span className="font-semibold text-amber-200">No Capture Group Defined in Search Pattern:</span>
                                                    <p className="text-[10px] text-amber-300/90 mt-0.5 leading-relaxed">
                                                        Replacement contains <code>$1</code>, but your Search Pattern does not have parentheses <code>( ... )</code> to capture text.
                                                        In regex, <code>$1</code> is populated by the 1st capture group. Add parentheses around the portion you wish to keep, e.g.: <code>^([A-Za-z_]+)\d&#123;2&#125;$</code>.
                                                    </p>
                                                </div>
                                            </div>
                                        )}

                                        <div className="flex items-center justify-between pt-1.5 border-t border-slate-800/60 font-sans">
                                            <span className="text-slate-400 font-medium">Transformed Target:</span>
                                            <span className="font-mono font-bold text-sky-300 text-sm">
                                                {simResult?.transformed || simInput}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex items-center justify-between pt-3 border-t border-slate-800 mt-1">
                                        {editingTransform ? (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={handleResetSimulator}
                                                    className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 rounded-lg text-xs text-slate-300 transition-colors"
                                                >
                                                    Cancel Edit
                                                </button>
                                                <div className="flex gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={handleSaveNewTransform}
                                                        className="px-3 py-1.5 bg-sky-600/30 hover:bg-sky-600/50 text-sky-300 border border-sky-500/40 rounded-lg text-xs font-semibold transition-colors"
                                                    >
                                                        Save as New
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={handleUpdateActiveTransform}
                                                        className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-xs font-semibold text-white shadow-md transition-colors flex items-center gap-1"
                                                    >
                                                        <span className="material-symbols-outlined text-[15px]">save</span>
                                                        Update Rule
                                                    </button>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="w-full flex justify-end">
                                                <button
                                                    type="button"
                                                    onClick={handleSaveNewTransform}
                                                    className="px-5 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 rounded-lg text-xs font-bold text-white shadow-md transition-all flex items-center gap-1.5"
                                                >
                                                    <span className="material-symbols-outlined text-[16px]">add_circle</span>
                                                    Save Transform Rule
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ========================================================= */}
                    {/* TAB 3: ENTITIES, FIELDS & CATEGORIES */}
                    {/* ========================================================= */}
                    {activeTab === 'entities' && (
                        <div className="h-full flex flex-col gap-4 overflow-hidden">
                            {/* Filter Bar with Combobox / Custom App & Version */}
                            <div className="flex items-center justify-between bg-slate-950/60 p-3 rounded-xl border border-slate-800 text-xs">
                                <div className="flex items-center gap-4 flex-wrap">
                                    {/* Application Selector */}
                                    <div className="flex items-center gap-2">
                                        <label className="text-slate-400 font-semibold font-sans">Source App:</label>
                                        <SearchableSelect
                                            options={appOptions}
                                            value={selectedAppId}
                                            onChange={(val) => setSelectedAppId(parseInt(val, 10))}
                                            placeholder="Select Application..."
                                            style={{ minWidth: 180 }}
                                        />
                                        {isCustomApp ? (
                                            <div className="flex items-center gap-1.5">
                                                <input
                                                    type="text"
                                                    placeholder="New App Name..."
                                                    value={customAppName}
                                                    onChange={(e) => setCustomAppName(e.target.value)}
                                                    className="bg-slate-900 border border-teal-500 rounded px-2.5 py-1 text-white text-xs w-36 font-sans"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={handleCreateCustomApp}
                                                    className="px-2 py-1 bg-teal-600 hover:bg-teal-500 text-white rounded text-xs font-semibold shadow-sm"
                                                >
                                                    Save
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setIsCustomApp(false)}
                                                    className="text-slate-400 hover:text-white text-xs"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => setIsCustomApp(true)}
                                                className="px-2 py-1 bg-slate-800 hover:bg-slate-700 text-teal-300 rounded text-xs font-semibold border border-slate-700 transition-colors"
                                                title="Add New Application"
                                            >
                                                + App
                                            </button>
                                        )}
                                    </div>

                                    {/* App Version Dropdown + Custom Input */}
                                    <div className="flex items-center gap-2">
                                        <label className="text-slate-400 font-semibold font-sans">Version:</label>
                                        <SearchableSelect
                                            options={entityVersionOptions}
                                            value={selectedVersion}
                                            onChange={async (val) => {
                                                const trimmed = String(val || '').trim();
                                                const existing = appVersions.find(v => v.VersionNumber === trimmed);
                                                if (existing) {
                                                    setSelectedVersion(trimmed);
                                                } else if (trimmed) {
                                                    try {
                                                        const res = await fetch(`/api/catalog/applications/${selectedAppId}/versions`, {
                                                            method: 'POST',
                                                            headers: authHeaders,
                                                            body: JSON.stringify({ VersionNumber: trimmed })
                                                        });
                                                        const data = await res.json();
                                                        if (data.success) {
                                                            showNotify(`Created and switched to version "${trimmed}"!`);
                                                            const vRes = await fetch(`/api/catalog/applications/${selectedAppId}/versions`, { headers: authHeaders });
                                                            const vData = await vRes.json();
                                                            if (vData.success && vData.data) setAppVersions(vData.data);
                                                            setSelectedVersion(trimmed);
                                                        } else {
                                                            showNotify(data.error || 'Failed to create version', 'error');
                                                        }
                                                    } catch (err) {
                                                        showNotify(err.message, 'error');
                                                    }
                                                }
                                            }}
                                            allowCustom={true}
                                            customPrompt="Add / Use version"
                                            placeholder="Select or enter version..."
                                            style={{ minWidth: 140 }}
                                        />
                                        {selectedVersion && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const verObj = appVersions.find(v => v.VersionNumber === selectedVersion);
                                                    if (verObj) handleDeleteVersion(verObj);
                                                }}
                                                className="p-1 text-slate-500 hover:text-rose-400 rounded transition-colors"
                                                title={`Delete version v${selectedVersion} and all descendants`}
                                            >
                                                <span className="material-symbols-outlined text-[16px]">delete</span>
                                            </button>
                                        )}
                                    </div>

                                    {/* Category Filter */}
                                    <div className="flex items-center gap-2">
                                        <label className="text-slate-400 font-semibold font-sans">Category Filter:</label>
                                        <select
                                            value={selectedCategoryId}
                                            onChange={(e) => setSelectedCategoryId(e.target.value)}
                                            className="bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1 text-white font-sans"
                                        >
                                            <option value="">All Categories</option>
                                            {categories.map((c) => (
                                                <option key={c.CategoryID} value={c.CategoryID}>
                                                    {c.CategoryName}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2">
                                    <input
                                        type="text"
                                        placeholder="Search entity..."
                                        value={entitySearch}
                                        onChange={(e) => setEntitySearch(e.target.value)}
                                        className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-1 text-white w-48 font-sans"
                                    />
                                    <button
                                        type="button"
                                        onClick={loadEntities}
                                        className="p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg border border-slate-700"
                                        title="Refresh Entities"
                                    >
                                        <span className="material-symbols-outlined text-[16px]">sync</span>
                                    </button>
                                </div>
                            </div>

                            {/* Split View: Left Entities, Right Fields & Direct Category Pills */}
                            <div className="flex-1 grid grid-cols-12 gap-6 overflow-hidden">
                                {/* Entity Sidebar */}
                                <div className="col-span-4 border border-slate-800 rounded-xl bg-slate-950/40 flex flex-col overflow-hidden">
                                    <div className="p-3 bg-slate-900 border-b border-slate-800 text-xs font-semibold text-slate-400 flex items-center justify-between">
                                        <span>Entities ({entities.length})</span>
                                        <span className="text-[10px] text-slate-500 font-mono">App #{selectedAppId} v{selectedVersion}</span>
                                    </div>
                                    <div className="flex-1 overflow-y-auto divide-y divide-slate-800/60 font-mono text-xs">
                                        {entities
                                            .filter(e => !entitySearch || (e.EntityCode || e.AbstractTableName || '').toLowerCase().includes(entitySearch.toLowerCase()))
                                            .map((e) => {
                                                const entCode = e.EntityCode || e.AbstractTableName;
                                                const isSelected = selectedEntity === entCode;
                                                return (
                                                    <div
                                                        key={e.EntityID || entCode}
                                                        onClick={() => selectEntity(entCode)}
                                                        className={`p-3 cursor-pointer flex items-center justify-between transition-colors ${
                                                            isSelected ? 'bg-emerald-950/40 border-l-4 border-emerald-500' : 'hover:bg-slate-800/40'
                                                        }`}
                                                    >
                                                        <div className="min-w-0 flex-1">
                                                            <div className="font-bold text-white flex items-center gap-1.5">
                                                                <span className="truncate">{entCode}</span>
                                                                <span className="text-[10px] text-slate-400 bg-slate-800 px-1.5 py-0.5 rounded font-sans flex-shrink-0">
                                                                    {e.FieldCount || e.ColumnCount || 0} cols
                                                                </span>
                                                            </div>
                                                            {e.DisplayName && e.DisplayName !== entCode && (
                                                                <div className="text-[11px] text-slate-400 font-sans truncate mt-0.5">
                                                                    {e.DisplayName}
                                                                </div>
                                                            )}
                                                            {(e.CategoriesList || e.Categories) && (
                                                                <div className="text-[10px] text-indigo-400 font-sans mt-0.5 truncate">
                                                                    {e.CategoriesList || e.Categories}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                                                            <button
                                                                type="button"
                                                                onClick={(ev) => {
                                                                    ev.stopPropagation();
                                                                    handleDeleteEntity(e.EntityID, entCode);
                                                                }}
                                                                className="p-1 text-slate-500 hover:text-rose-400 rounded transition-colors"
                                                                title={`Delete entity "${entCode}" and all its fields`}
                                                            >
                                                                <span className="material-symbols-outlined text-[15px]">delete</span>
                                                            </button>
                                                            <span className="material-symbols-outlined text-slate-600 text-[16px]">chevron_right</span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                </div>

                                {/* Fields Grid & Inline Category Assignment */}
                                <div className="col-span-8 border border-slate-800 rounded-xl bg-slate-950/40 flex flex-col overflow-hidden">
                                    {/* Header */}
                                    <div className="p-3 bg-slate-900 border-b border-slate-800 flex items-center justify-between">
                                        <div className="text-xs font-bold text-white flex items-center gap-2">
                                            <span className="text-slate-400 font-sans">Entity:</span>
                                            <span className="text-emerald-400 font-mono text-sm">{selectedEntity || 'Select an Entity'}</span>
                                            {selectedEntity && (
                                                <span className="text-[11px] text-slate-400 font-sans">
                                                    ({fields.length} columns)
                                                </span>
                                            )}
                                        </div>
                                        {selectedEntity && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const entObj = entities.find(e => (e.EntityCode || e.AbstractTableName) === selectedEntity);
                                                    if (entObj) handleDeleteEntity(entObj.EntityID, selectedEntity);
                                                }}
                                                className="flex items-center gap-1 px-2.5 py-1 bg-rose-950/40 hover:bg-rose-900/60 border border-rose-800/60 text-rose-300 hover:text-rose-200 rounded text-xs transition-colors"
                                                title={`Delete entity ${selectedEntity}`}
                                            >
                                                <span className="material-symbols-outlined text-[14px]">delete</span>
                                                Delete Entity
                                            </button>
                                        )}
                                    </div>

                                    {/* Direct Category Assignment Pills Strip */}
                                    {selectedEntity && (
                                        <div className="p-2.5 bg-slate-900/60 border-b border-slate-800 flex items-center gap-2 flex-wrap">
                                            <span className="text-[11px] font-semibold text-slate-400 flex items-center gap-1 font-sans">
                                                <span className="material-symbols-outlined text-[14px] text-indigo-400">label</span>
                                                Categories:
                                            </span>
                                            {categories.map((cat) => {
                                                const isAssigned = selectedEntityCatIds.includes(cat.CategoryID);
                                                return (
                                                    <button
                                                        key={cat.CategoryID}
                                                        type="button"
                                                        onClick={() => handleToggleCategory(cat.CategoryID)}
                                                        className={`px-2.5 py-0.5 rounded-full text-[11px] font-sans font-medium flex items-center gap-1 transition-all ${
                                                            isAssigned
                                                                ? 'bg-indigo-600 text-white shadow-sm border border-indigo-400'
                                                                : 'bg-slate-900 text-slate-400 border border-slate-700/80 hover:border-slate-500 hover:text-slate-200'
                                                        }`}
                                                        title={cat.Description || cat.CategoryName}
                                                    >
                                                        <span className="material-symbols-outlined text-[13px]">
                                                            {isAssigned ? 'check_circle' : 'add'}
                                                        </span>
                                                        {cat.CategoryName}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* Fields Table */}
                                    <div className="flex-1 overflow-y-auto">
                                        <table className="w-full text-left text-xs text-slate-300">
                                            <thead className="bg-slate-900 text-slate-400 sticky top-0 border-b border-slate-800">
                                                <tr>
                                                    <th className="py-2.5 px-3">Column Name / Dot-Path</th>
                                                    <th className="py-2.5 px-3">Type</th>
                                                    <th className="py-2.5 px-3">Nullable</th>
                                                    <th className="py-2.5 px-3">Description</th>
                                                    <th className="py-2.5 px-3 text-right">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-800/60 font-mono">
                                                {fields.map((f) => (
                                                    <tr key={f.FieldID} className="hover:bg-slate-800/40 transition-colors">
                                                        <td className="py-2 px-3 font-semibold text-white">{f.ColumnName}</td>
                                                        <td className="py-2 px-3 text-emerald-400">
                                                            {f.DataType}
                                                            {f.LengthOrPrecision ? `(${f.LengthOrPrecision})` : ''}
                                                        </td>
                                                        <td className="py-2 px-3 text-slate-400">{f.IsNullable ? 'YES' : 'NO'}</td>
                                                        <td className="py-2 px-3 font-sans">
                                                            {editingFieldId === f.FieldID ? (
                                                                <input
                                                                    type="text"
                                                                    value={editingFieldDescription}
                                                                    onChange={(e) => setEditingFieldDescription(e.target.value)}
                                                                    className="bg-slate-900 border border-slate-700 rounded px-2 py-0.5 text-xs text-white w-full"
                                                                />
                                                            ) : (
                                                                <span className="text-slate-300">{f.FieldDescription || '—'}</span>
                                                            )}
                                                        </td>
                                                        <td className="py-2 px-3 text-right">
                                                            {editingFieldId === f.FieldID ? (
                                                                <div className="flex items-center justify-end gap-1">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleSaveField(f.FieldID, f.IsNullable)}
                                                                        className="px-2 py-0.5 bg-emerald-600 text-white rounded text-[11px]"
                                                                    >
                                                                        Save
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setEditingFieldId(null)}
                                                                        className="px-2 py-0.5 bg-slate-800 text-slate-400 rounded text-[11px]"
                                                                    >
                                                                        Cancel
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <div className="flex items-center justify-end gap-1">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setEditingFieldId(f.FieldID);
                                                                            setEditingFieldDescription(f.FieldDescription || '');
                                                                        }}
                                                                        className="p-1 text-slate-500 hover:text-white"
                                                                        title="Edit Description"
                                                                    >
                                                                        <span className="material-symbols-outlined text-[15px]">edit</span>
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleDeleteField(f.FieldID, f.ColumnName)}
                                                                        className="p-1 text-slate-500 hover:text-rose-400"
                                                                        title="Delete Field"
                                                                    >
                                                                        <span className="material-symbols-outlined text-[15px]">delete</span>
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ========================================================= */}
                    {/* TAB 4: RELATIONSHIPS */}
                    {/* ========================================================= */}
                    {activeTab === 'relationships' && (
                        <div className="h-full flex flex-col gap-4 overflow-hidden">
                            <div className="flex items-center justify-between bg-slate-950/40 p-4 rounded-xl border border-slate-800">
                                <div>
                                    <h3 className="text-sm font-bold text-white">Complex Relational Boundaries</h3>
                                    <p className="text-xs text-slate-400">
                                        Multi-part composite keys and delimited concatenated joins enabling automated multi-hop query resolution.
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <SearchableSelect
                                        options={appOptions}
                                        value={selectedAppId}
                                        onChange={(val) => setSelectedAppId(parseInt(val, 10))}
                                        placeholder="Select Application..."
                                        style={{ minWidth: 150 }}
                                    />
                                    <SearchableSelect
                                        options={entityVersionOptions}
                                        value={selectedVersion}
                                        onChange={(val) => setSelectedVersion(val)}
                                        placeholder="Select Version..."
                                        style={{ minWidth: 120 }}
                                    />
                                    <input
                                        type="text"
                                        placeholder="Filter by table..."
                                        value={relFilterTable}
                                        onChange={(e) => setRelFilterTable(e.target.value)}
                                        className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-white"
                                    />
                                    <button
                                        onClick={() => setIsCreatingRel(true)}
                                        className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-semibold"
                                    >
                                        <span className="material-symbols-outlined text-[16px]">add</span>
                                        New Relationship
                                    </button>
                                </div>
                            </div>

                            <div className="flex-1 overflow-y-auto grid grid-cols-2 gap-4">
                                {relationships.map((r) => (
                                    <div key={r.RelationshipID} className="bg-slate-950 border border-slate-800 rounded-xl p-4 flex flex-col justify-between">
                                        <div>
                                            <div className="flex items-center justify-between pb-2 border-b border-slate-800">
                                                <div className="font-bold text-white font-mono text-sm flex items-center gap-2">
                                                    <span>{r.FromTable}</span>
                                                    <span className="material-symbols-outlined text-[14px] text-emerald-400">arrow_forward</span>
                                                    <span>{r.ToTable}</span>
                                                </div>
                                                <span className="px-2 py-0.5 rounded text-[10px] bg-slate-800 text-amber-300 border border-slate-700 font-sans font-bold">
                                                    {r.Cardinality} ({r.JoinType})
                                                </span>
                                            </div>
                                            <div className="text-xs text-slate-400 mt-2 mb-3">
                                                {r.RelationshipName}
                                            </div>

                                            {/* Components */}
                                            <div className="space-y-1.5 font-mono text-xs">
                                                {(r.components || []).map((comp) => (
                                                    <div key={comp.ComponentID} className="flex items-center gap-2 bg-slate-900/80 px-2.5 py-1 rounded border border-slate-800/60">
                                                        <span className="text-slate-500 text-[10px]">#{comp.KeyOrdinal}</span>
                                                        <span className="text-sky-300">{comp.FromColumn}</span>
                                                        <span className="text-slate-600">=</span>
                                                        <span className="text-emerald-300">{comp.ToColumn}</span>
                                                        {comp.Delimiter && (
                                                            <span className="text-[10px] bg-purple-950 text-purple-300 px-1 rounded ml-auto">
                                                                Delimiter: '{comp.Delimiter}'
                                                            </span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-end gap-2 pt-3 mt-3 border-t border-slate-900 text-xs">
                                            <button
                                                onClick={() => handleDeleteRelationship(r.RelationshipID)}
                                                className="text-slate-500 hover:text-red-400 flex items-center gap-1"
                                            >
                                                <span className="material-symbols-outlined text-[15px]">delete</span>
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal: Regex Cheatsheet */}
            {showRegexCheatsheet && (
                <div 
                    className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setShowRegexCheatsheet(false);
                    }}
                >
                    <div className="bg-slate-900 border border-slate-700 w-full max-w-2xl max-h-[88vh] rounded-2xl p-6 flex flex-col gap-4 text-white shadow-2xl animate-fade-in">
                        {/* Header */}
                        <div className="flex items-center justify-between border-b border-slate-800 pb-3">
                            <div className="flex items-center gap-2.5">
                                <div className="p-2 bg-teal-500/10 border border-teal-500/30 rounded-xl text-teal-400">
                                    <span className="material-symbols-outlined text-xl">menu_book</span>
                                </div>
                                <div>
                                    <h3 className="text-sm font-bold text-white flex items-center gap-2">
                                        Regex Rules &amp; Transform Reference Guide
                                    </h3>
                                    <p className="text-[11px] text-slate-400">
                                        Master regular expression syntax, capture groups, and backreference variables.
                                    </p>
                                </div>
                            </div>
                            <button 
                                onClick={() => setShowRegexCheatsheet(false)} 
                                className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                            >
                                <span className="material-symbols-outlined text-[20px]">close</span>
                            </button>
                        </div>

                        {/* Scrollable Content Body */}
                        <div className="flex-1 overflow-y-auto space-y-4 pr-1 text-xs font-sans">
                            {/* Key Concept: Capture Groups & $1 / $2 */}
                            <div className="p-3.5 rounded-xl bg-gradient-to-r from-emerald-950/70 to-teal-950/50 border border-emerald-700/60 flex flex-col gap-2">
                                <div className="flex items-center gap-2 text-emerald-300 font-bold text-[13px]">
                                    <span className="material-symbols-outlined text-[18px]">group_work</span>
                                    How Capture Groups &amp; $1, $2 Work
                                </div>
                                <p className="text-slate-300 text-[11px] leading-relaxed">
                                    In regular expressions, wrapping a portion of your pattern in parentheses <code className="bg-slate-900 px-1.5 py-0.5 rounded text-emerald-400 font-mono">( ... )</code> creates a <strong>Capture Group</strong>. The text matched inside is automatically assigned to a backreference variable:
                                </p>
                                <div className="grid grid-cols-3 gap-2 font-mono text-[11px] mt-1">
                                    <div className="p-2 rounded bg-slate-900/90 border border-slate-800">
                                        <div className="text-emerald-400 font-bold">$1</div>
                                        <div className="text-[10px] text-slate-400 font-sans mt-0.5">1st pair of <code className="text-emerald-300">( ... )</code></div>
                                    </div>
                                    <div className="p-2 rounded bg-slate-900/90 border border-slate-800">
                                        <div className="text-sky-400 font-bold">$2</div>
                                        <div className="text-[10px] text-slate-400 font-sans mt-0.5">2nd pair of <code className="text-sky-300">( ... )</code></div>
                                    </div>
                                    <div className="p-2 rounded bg-slate-900/90 border border-slate-800">
                                        <div className="text-amber-400 font-bold">$&amp;</div>
                                        <div className="text-[10px] text-slate-400 font-sans mt-0.5">Entire matched text</div>
                                    </div>
                                </div>
                                <div className="p-2.5 rounded-lg bg-amber-950/40 border border-amber-800/60 text-amber-200 text-[11px] mt-1 flex items-start gap-1.5">
                                    <span className="material-symbols-outlined text-[16px] text-amber-400 flex-shrink-0 mt-0.5">info</span>
                                    <div>
                                        <strong>Why does my transform show literal &quot;$1&quot;?</strong>
                                        <div className="text-[10px] text-amber-300/90 mt-0.5 leading-relaxed">
                                            If your Search Pattern has <em>no parentheses</em> (e.g. <code className="text-white bg-slate-900 px-1 py-0.5 rounded font-mono">^CUSFIL\d&#123;2&#125;$</code>), there is no group 1 to capture! The engine will replace the match with the literal characters &quot;$1&quot;. Wrap the part you want to keep in parentheses: <code className="text-emerald-300 bg-slate-900 px-1 py-0.5 rounded font-mono">^([A-Za-z_]+)\d&#123;2&#125;$</code>.
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Metacharacters & Character Classes */}
                            <div className="border border-slate-800 rounded-xl overflow-hidden bg-slate-950/50">
                                <div className="px-3 py-2 bg-slate-900/80 border-b border-slate-800 font-bold text-slate-200 flex items-center gap-1.5">
                                    <span className="material-symbols-outlined text-[16px] text-sky-400">code</span>
                                    Character Classes &amp; Metacharacters
                                </div>
                                <div className="p-3 grid grid-cols-2 gap-2 text-[11px] font-mono">
                                    <div className="flex items-center gap-2 p-1.5 rounded bg-slate-900 border border-slate-800/80">
                                        <span className="text-emerald-400 font-bold w-12 text-center">\d</span>
                                        <span className="text-slate-300 font-sans">Any digit (0-9)</span>
                                    </div>
                                    <div className="flex items-center gap-2 p-1.5 rounded bg-slate-900 border border-slate-800/80">
                                        <span className="text-emerald-400 font-bold w-12 text-center">\D</span>
                                        <span className="text-slate-300 font-sans">Any non-digit character</span>
                                    </div>
                                    <div className="flex items-center gap-2 p-1.5 rounded bg-slate-900 border border-slate-800/80">
                                        <span className="text-emerald-400 font-bold w-12 text-center">\w</span>
                                        <span className="text-slate-300 font-sans">Word char (A-Z, a-z, 0-9, _)</span>
                                    </div>
                                    <div className="flex items-center gap-2 p-1.5 rounded bg-slate-900 border border-slate-800/80">
                                        <span className="text-emerald-400 font-bold w-12 text-center">\W</span>
                                        <span className="text-slate-300 font-sans">Non-word char (punctuation, space)</span>
                                    </div>
                                    <div className="flex items-center gap-2 p-1.5 rounded bg-slate-900 border border-slate-800/80">
                                        <span className="text-emerald-400 font-bold w-12 text-center">\s</span>
                                        <span className="text-slate-300 font-sans">Any whitespace (space, tab)</span>
                                    </div>
                                    <div className="flex items-center gap-2 p-1.5 rounded bg-slate-900 border border-slate-800/80">
                                        <span className="text-emerald-400 font-bold w-12 text-center">.</span>
                                        <span className="text-slate-300 font-sans">Any single character</span>
                                    </div>
                                    <div className="flex items-center gap-2 p-1.5 rounded bg-slate-900 border border-slate-800/80">
                                        <span className="text-emerald-400 font-bold w-12 text-center">[A-Z]</span>
                                        <span className="text-slate-300 font-sans">Match any char in brackets</span>
                                    </div>
                                    <div className="flex items-center gap-2 p-1.5 rounded bg-slate-900 border border-slate-800/80">
                                        <span className="text-emerald-400 font-bold w-12 text-center">[^0-9]</span>
                                        <span className="text-slate-300 font-sans">Negated set (anything but digits)</span>
                                    </div>
                                </div>
                            </div>

                            {/* Anchors & Quantifiers */}
                            <div className="grid grid-cols-2 gap-3">
                                {/* Anchors */}
                                <div className="border border-slate-800 rounded-xl overflow-hidden bg-slate-950/50">
                                    <div className="px-3 py-2 bg-slate-900/80 border-b border-slate-800 font-bold text-slate-200 flex items-center gap-1.5">
                                        <span className="material-symbols-outlined text-[16px] text-amber-400">anchor</span>
                                        Position Anchors
                                    </div>
                                    <div className="p-2.5 space-y-1.5 text-[11px] font-mono">
                                        <div className="flex items-center gap-2 p-1.5 rounded bg-slate-900 border border-slate-800/80">
                                            <span className="text-amber-400 font-bold w-8 text-center">^</span>
                                            <span className="text-slate-300 font-sans">Start of string/line</span>
                                        </div>
                                        <div className="flex items-center gap-2 p-1.5 rounded bg-slate-900 border border-slate-800/80">
                                            <span className="text-amber-400 font-bold w-8 text-center">$</span>
                                            <span className="text-slate-300 font-sans">End of string/line</span>
                                        </div>
                                        <div className="flex items-center gap-2 p-1.5 rounded bg-slate-900 border border-slate-800/80">
                                            <span className="text-amber-400 font-bold w-8 text-center">\b</span>
                                            <span className="text-slate-300 font-sans">Word boundary</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Quantifiers */}
                                <div className="border border-slate-800 rounded-xl overflow-hidden bg-slate-950/50">
                                    <div className="px-3 py-2 bg-slate-900/80 border-b border-slate-800 font-bold text-slate-200 flex items-center gap-1.5">
                                        <span className="material-symbols-outlined text-[16px] text-purple-400">repeat</span>
                                        Quantifiers
                                    </div>
                                    <div className="p-2.5 space-y-1.5 text-[11px] font-mono">
                                        <div className="flex items-center gap-2 p-1.5 rounded bg-slate-900 border border-slate-800/80">
                                            <span className="text-purple-400 font-bold w-12 text-center">+</span>
                                            <span className="text-slate-300 font-sans">1 or more times</span>
                                        </div>
                                        <div className="flex items-center gap-2 p-1.5 rounded bg-slate-900 border border-slate-800/80">
                                            <span className="text-purple-400 font-bold w-12 text-center">*</span>
                                            <span className="text-slate-300 font-sans">0 or more times</span>
                                        </div>
                                        <div className="flex items-center gap-2 p-1.5 rounded bg-slate-900 border border-slate-800/80">
                                            <span className="text-purple-400 font-bold w-12 text-center">?</span>
                                            <span className="text-slate-300 font-sans">0 or 1 time (optional)</span>
                                        </div>
                                        <div className="flex items-center gap-2 p-1.5 rounded bg-slate-900 border border-slate-800/80">
                                            <span className="text-purple-400 font-bold w-12 text-center">&#123;2&#125;</span>
                                            <span className="text-slate-300 font-sans">Exactly 2 times (e.g. \d&#123;2&#125;)</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Practical ERP Examples */}
                            <div className="border border-slate-800 rounded-xl overflow-hidden bg-slate-950/50">
                                <div className="px-3 py-2 bg-slate-900/80 border-b border-slate-800 font-bold text-slate-200 flex items-center gap-1.5">
                                    <span className="material-symbols-outlined text-[16px] text-teal-400">auto_fix_high</span>
                                    Practical ERP &amp; Database Examples
                                </div>
                                <div className="p-3 space-y-2 font-mono text-[11px]">
                                    {/* Example 1 */}
                                    <div className="p-2.5 rounded-lg bg-slate-900 border border-slate-800">
                                        <div className="flex items-center justify-between">
                                            <span className="text-emerald-400 font-bold">^([A-Za-z_]+)\d&#123;2&#125;$</span>
                                            <span className="text-sky-400 font-bold">&rarr; $1</span>
                                        </div>
                                        <div className="text-[10px] text-slate-400 font-sans mt-1">
                                            Strips 2-digit company suffix: <code className="text-white">CUSFIL00</code> &rarr; <code className="text-emerald-400 font-bold">CUSFIL</code>, <code className="text-white">ARCUST26</code> &rarr; <code className="text-emerald-400 font-bold">ARCUST</code>.
                                        </div>
                                    </div>

                                    {/* Example 2 */}
                                    <div className="p-2.5 rounded-lg bg-slate-900 border border-slate-800">
                                        <div className="flex items-center justify-between">
                                            <span className="text-emerald-400 font-bold">^([A-Z]&#123;2&#125;)_([A-Za-z]+)$</span>
                                            <span className="text-sky-400 font-bold">&rarr; $2</span>
                                        </div>
                                        <div className="text-[10px] text-slate-400 font-sans mt-1">
                                            Strips module prefix: <code className="text-white">GL_ACCOUNTS</code> &rarr; <code className="text-emerald-400 font-bold">ACCOUNTS</code>, <code className="text-white">OE_ORDERS</code> &rarr; <code className="text-emerald-400 font-bold">ORDERS</code>.
                                        </div>
                                    </div>

                                    {/* Example 3 */}
                                    <div className="p-2.5 rounded-lg bg-slate-900 border border-slate-800">
                                        <div className="flex items-center justify-between">
                                            <span className="text-emerald-400 font-bold">^pbsdata\d&#123;2&#125;$</span>
                                            <span className="text-sky-400 font-bold">&rarr; PASSPORT</span>
                                        </div>
                                        <div className="text-[10px] text-slate-400 font-sans mt-1">
                                            Autodetects Passport database naming convention: <code className="text-white">pbsdata00</code> &rarr; <code className="text-emerald-400 font-bold">PASSPORT</code>.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between pt-3 border-t border-slate-800">
                            <span className="text-[11px] text-slate-400 font-sans">
                                Tip: You can test any pattern instantly in the Live Simulator on the right.
                            </span>
                            <button 
                                onClick={() => setShowRegexCheatsheet(false)} 
                                className="px-4 py-1.5 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-xs font-semibold transition-colors"
                            >
                                Close Guide
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal: New Relational Boundary */}
            {isCreatingRel && (
                <div 
                    className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setIsCreatingRel(false);
                    }}
                >
                    <div className="bg-slate-900 border border-slate-700 w-full max-w-2xl max-h-[90vh] rounded-2xl p-5 flex flex-col gap-4 text-white shadow-2xl animate-fade-in">
                        {/* Header */}
                        <div className="flex items-center justify-between border-b border-slate-800 pb-3">
                            <div className="flex items-center gap-2.5">
                                <div className="p-2 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-emerald-400">
                                    <span className="material-symbols-outlined text-xl">account_tree</span>
                                </div>
                                <div>
                                    <h3 className="text-sm font-bold text-white flex items-center gap-2">
                                        New Relational Boundary
                                    </h3>
                                    <p className="text-[11px] text-slate-400">
                                        Define implicit foreign keys, composite multi-column joins, and ordinal mappings.
                                    </p>
                                </div>
                            </div>
                            <button 
                                onClick={() => setIsCreatingRel(false)} 
                                className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                            >
                                <span className="material-symbols-outlined text-[20px]">close</span>
                            </button>
                        </div>

                        {/* Form */}
                        <form onSubmit={handleSaveRelationship} className="flex-1 overflow-y-auto space-y-4 pr-1 text-xs">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="col-span-2">
                                    <label className="block text-[11px] font-semibold text-slate-300 uppercase tracking-wider mb-1">
                                        Relationship Name *
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        placeholder="e.g. FK_OEHDR_CUSFIL_CUSTOMER"
                                        value={relForm.RelationshipName}
                                        onChange={(e) => setRelForm(prev => ({ ...prev, RelationshipName: e.target.value }))}
                                        className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-white font-mono focus:border-emerald-500 focus:outline-none"
                                    />
                                </div>

                                <div>
                                    <label className="block text-[11px] font-semibold text-slate-300 uppercase tracking-wider mb-1">
                                        From Table (Source) *
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        placeholder="e.g. OEHDR00"
                                        value={relForm.FromTable}
                                        onChange={(e) => setRelForm(prev => ({ ...prev, FromTable: e.target.value }))}
                                        className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-white font-mono focus:border-emerald-500 focus:outline-none"
                                    />
                                </div>

                                <div>
                                    <label className="block text-[11px] font-semibold text-slate-300 uppercase tracking-wider mb-1">
                                        To Table (Target) *
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        placeholder="e.g. CUSFIL00"
                                        value={relForm.ToTable}
                                        onChange={(e) => setRelForm(prev => ({ ...prev, ToTable: e.target.value }))}
                                        className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-white font-mono focus:border-emerald-500 focus:outline-none"
                                    />
                                </div>

                                <div>
                                    <label className="block text-[11px] font-semibold text-slate-300 uppercase tracking-wider mb-1">
                                        Cardinality
                                    </label>
                                    <select
                                        value={relForm.Cardinality}
                                        onChange={(e) => setRelForm(prev => ({ ...prev, Cardinality: e.target.value }))}
                                        className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-white focus:border-emerald-500 focus:outline-none"
                                    >
                                        <option value="1:1">1:1 (One-to-One)</option>
                                        <option value="1:M">1:M (One-to-Many)</option>
                                        <option value="M:1">M:1 (Many-to-One)</option>
                                        <option value="M:M">M:M (Many-to-Many)</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-[11px] font-semibold text-slate-300 uppercase tracking-wider mb-1">
                                        Join Type
                                    </label>
                                    <select
                                        value={relForm.JoinType}
                                        onChange={(e) => setRelForm(prev => ({ ...prev, JoinType: e.target.value }))}
                                        className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-white focus:border-emerald-500 focus:outline-none"
                                    >
                                        <option value="INNER">INNER JOIN</option>
                                        <option value="LEFT">LEFT JOIN</option>
                                        <option value="RIGHT">RIGHT JOIN</option>
                                        <option value="FULL">FULL OUTER JOIN</option>
                                    </select>
                                </div>

                                <div className="col-span-2">
                                    <label className="block text-[11px] font-semibold text-slate-300 uppercase tracking-wider mb-1">
                                        Description
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Optional documentation of business rule or relationship origin"
                                        value={relForm.Description}
                                        onChange={(e) => setRelForm(prev => ({ ...prev, Description: e.target.value }))}
                                        className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-white focus:border-emerald-500 focus:outline-none"
                                    />
                                </div>
                            </div>

                            {/* Composite Key Components */}
                            <div className="space-y-2 pt-2 border-t border-slate-800">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h4 className="font-semibold text-white text-xs">Composite Key Components</h4>
                                        <p className="text-[10px] text-slate-400">Map columns forming this composite relationship in ordinal sequence.</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setRelForm(prev => ({
                                            ...prev,
                                            components: [
                                                ...prev.components,
                                                { KeyOrdinal: prev.components.length + 1, FromColumn: '', ToColumn: '', Delimiter: '' }
                                            ]
                                        }))}
                                        className="flex items-center gap-1 px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-sky-400 rounded text-[11px] font-semibold border border-slate-700 transition-colors"
                                    >
                                        <span className="material-symbols-outlined text-[14px]">add</span>
                                        Add Component
                                    </button>
                                </div>

                                <div className="space-y-1.5">
                                    {relForm.components.map((comp, idx) => (
                                        <div key={idx} className="flex items-center gap-2 bg-slate-950 p-2 rounded-lg border border-slate-800 font-mono text-xs">
                                            <span className="text-slate-500 text-[10px] w-5">#{comp.KeyOrdinal}</span>
                                            <input
                                                type="text"
                                                required
                                                placeholder="From Column (e.g. CUS_NO)"
                                                value={comp.FromColumn}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    setRelForm(prev => ({
                                                        ...prev,
                                                        components: prev.components.map((c, i) => i === idx ? { ...c, FromColumn: val } : c)
                                                    }));
                                                }}
                                                className="flex-1 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sky-300 focus:outline-none focus:border-emerald-500"
                                            />
                                            <span className="text-slate-500 font-bold">=</span>
                                            <input
                                                type="text"
                                                required
                                                placeholder="To Column (e.g. CUS_NO)"
                                                value={comp.ToColumn}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    setRelForm(prev => ({
                                                        ...prev,
                                                        components: prev.components.map((c, i) => i === idx ? { ...c, ToColumn: val } : c)
                                                    }));
                                                }}
                                                className="flex-1 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-emerald-300 focus:outline-none focus:border-emerald-500"
                                            />
                                            <input
                                                type="text"
                                                placeholder="Delimiter (opt)"
                                                value={comp.Delimiter || ''}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    setRelForm(prev => ({
                                                        ...prev,
                                                        components: prev.components.map((c, i) => i === idx ? { ...c, Delimiter: val } : c)
                                                    }));
                                                }}
                                                className="w-24 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-purple-300 placeholder:text-slate-600 focus:outline-none focus:border-emerald-500"
                                            />
                                            {relForm.components.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setRelForm(prev => ({
                                                            ...prev,
                                                            components: prev.components.filter((_, i) => i !== idx).map((c, i) => ({ ...c, KeyOrdinal: i + 1 }))
                                                        }));
                                                    }}
                                                    className="text-slate-500 hover:text-red-400 p-1"
                                                >
                                                    <span className="material-symbols-outlined text-[16px]">close</span>
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Footer Actions */}
                            <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-800">
                                <button
                                    type="button"
                                    onClick={() => setIsCreatingRel(false)}
                                    className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-semibold transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 shadow transition-colors"
                                >
                                    <span className="material-symbols-outlined text-[16px]">save</span>
                                    Save Relationship
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
